// DrawingCanvas — React wrapper that PROVIDES the <canvas> element.
//
// Boundary contract (architecture.md §16 / §19.6):
//   - frontend-developer (this file): mounts a <canvas>, wires PointerEvent
//     handlers, calls `engine.startStroke / addPoints / endStroke`, hands
//     the engine off via `onEngineReady` so siblings can call
//     `applyRemoteStroke`.
//   - drawing-engine-developer (`infrastructure/drawing/`): owns whatever
//     happens INSIDE those engine methods (2層レンダリング, OffscreenCanvas
//     hand-off, etc.).
//
// In Phase 3 前半 the engine is a stub, so no pixels appear — but the
// pointer plumbing is real and emits the right wire events through the
// session transport.
//
// Phase 7 Task 7-8b — imagepaste preview integration:
//   - When `engine.isImagepastePreviewActive()` is true at `pointerdown`,
//     we route the gesture to the engine's preview-drag API instead of
//     starting a stroke. Hits inside the preview rect → drag; hits
//     outside → swallow (prevents accidental strokes underneath the
//     overlay buttons).
//   - The pointer-active state machine carries the gesture *kind* across
//     `pointerdown` → `pointermove` → `pointerup` so a single canvas
//     handler can serve both stroke and preview-drag without re-checking
//     the engine on every event.

import type { StrokePoint } from '@oekaki/protocol';
import { type RefObject, useEffect, useRef } from 'react';
import {
  computeEffectivePressure,
  DEFAULT_PEN_PRESSURE,
  type PenPressureSettings,
} from '../../../application/penPressure.js';
import type { Transport } from '../../../application/ports/Transport.js';
import {
  isPressureOn,
  type StrokeInputKind,
  strokeInputFromPointerType,
} from '../../../application/strokeInput.js';
import { clampTouchGestureDelayMs } from '../../../application/touchGestureAnalytics.js';
import {
  sendStrokeEnd,
  sendStrokeSegment,
  sendStrokeStart,
} from '../../../application/useCases.js';
import { generateAirbrushSeed, type PenConfig, penParamsFor } from '../../../domain/PenConfig.js';
import { createDotByDotPreferenceStore } from '../../../infrastructure/dotByDotPreference.js';
import { CanvasDrawingEngine } from '../../../infrastructure/drawing/CanvasDrawingEngine.js';
import {
  isInputLatencyEnabled,
  noteInputLatencyMark,
} from '../../../infrastructure/drawing/inputLatencyProbe.js';
import type { CanvasViewMapper } from '../../../infrastructure/drawing/viewport.js';
import {
  createRotationTracker,
  ROTATION_GESTURE_THRESHOLD_DEG,
  ROTATION_SNAP_DEG,
  type RotationTracker,
  vectorAngleDeg,
} from '../../../infrastructure/drawing/viewRotation.js';
// PR 2-β (2026-05-17) — Worker offload (architecture.md §19.16). Vite's
// `?worker` suffix splits this module into its own bundle and gives us a
// `Worker` constructor we hand to `CanvasDrawingEngine` via `workerFactory`.
// When the runtime doesn't support OffscreenCanvas / Worker /
// transferControlToOffscreen, the engine falls back to inline (= identical
// behaviour, just on the main thread).
import DrawingWorkerCtor from '../../../infrastructure/drawing/worker/drawingWorker.js?worker';
import type { RegionPointerSink } from '../../hooks/useRegionMove.js';
import { rgbToCss } from '../shared/canvasBackgroundStyle.js';
import {
  computeLeadSegmentPoints,
  type LeadLineSource,
  type LeadPathMode,
  type LeadSamplePoint,
} from './leadLine.js';
import { isPenEraserTipPointer } from './penEraserTip.js';
import {
  shouldAutoEnablePenOnlyMode,
  shouldShowPenOnlyFingerNotice,
  shouldSkipPointerForPenOnlyMode,
} from './penOnlyMode.js';
import {
  SpuitPickCoalescer,
  type SpuitSampleMode,
  shouldEnterSpuitDragMode,
  shouldPickOnPointerDown,
  shouldSkipStrokeOnPointerDown,
} from './spuit.js';
import {
  computeGestureFrame,
  type GesturePoint,
  gestureCentroid,
  gestureDistance,
  IDLE_PEN_ONLY_TOUCH_PHASE,
  IDLE_TOUCH_PHASE,
  lateTouchDelayRaw,
  type PenOnlyTouchEffect,
  type PenOnlyTouchEvent,
  type PenOnlyTouchPhase,
  TOUCH_GRACE_WINDOW_MS,
  type TouchFirstDown,
  type TouchGestureEffect,
  type TouchGestureEvent,
  type TouchGesturePhase,
  transitionPenOnlyTouch,
  transitionTouchGesture,
} from './touchGesture.js';

export interface DrawingCanvasProps {
  readonly width: number;
  readonly height: number;
  readonly pen: PenConfig;
  readonly transport: Transport;
  /**
   * Called with the constructed engine instance on mount, and again with
   * `null` on unmount so the SessionProvider can stop forwarding remote
   * strokes to a disposed engine. (architecture.md §19.6)
   */
  readonly onEngineReady?: (engine: CanvasDrawingEngine | null) => void;
  /**
   * Phase 7 Task 7-8b: optional ref forwarded to the underlying `<canvas>`
   * element so siblings (`ImagepasteOverlay`) can read the bounding rect
   * to position HTML overlays. Pure read-only — siblings MUST NOT mutate
   * the canvas (drawing is the engine's responsibility).
   */
  readonly canvasRef?: RefObject<HTMLCanvasElement | null>;
  /**
   * Phase 6.7 後追い (consult 2026-05-06) — スポイト (eyedropper) で拾った
   * RGB24 色を呼び出し側 (`ClassicLayout` → `setPen`) に流す callback.
   *
   * 発動条件: `contextmenu` (= 右クリック) または `pointerdown` で
   * `e.altKey === true` (= Alt+クリック). 発動時は通常の stroke 開始
   * フローを skip する.
   *
   * 取得色は `engine.getLayerPixelColor(layerId, x, y)` の戻り値
   * (= 選択中 layer の合成前ピクセル色). OOB / 未準備時は callback を
   * 呼ばない (caller 側は何もしなくて良い).
   *
   * undefined なら spuit 機能は無効化される (= 旧来通りの挙動).
   */
  readonly onSpuitColor?: (rgb24: number) => void;
  /**
   * §9.6 方針転換 (2026-06-12): スポイト**持続モード** (Modern 専用)。`true` のとき
   * キャンバスの **素 pointerdown** (Alt 不要) を色拾い経路 (`onSpuitColor`) に流し、
   * 通常のストローク開始を抑止する。選び直すまでモードは継続する (= caller が state を
   * 保持し、別ツール選択時に false に戻す)。
   *
   * 既存の Alt+クリック / 右クリックの瞬間ジェスチャ経路は本フラグと無関係にそのまま
   * 動く (= 両立)。Classic はこの prop を渡さない (= false、ジェスチャのみ・無影響)。
   *
   * 未指定なら `false` (= 後方互換、持続モードなし)。
   */
  readonly spuitMode?: boolean;
  /**
   * 2026-06-12 スポイト確定シグナル (最近色の記録厳密化)。
   *
   * スポイト操作 (持続モード / Alt+クリック / 右クリックいずれの経路でも) の **確定 =
   * pointerup / pointercancel** 時に、その操作で最後に拾った色 (rgb24) を1回だけ通知する。
   * caller (= ModernLayout) はこれを `useRecentColors.recordColor` に流し、最近色へ確定の
   * 1色だけを記録する (= ドラッグ中の通過色・静止色を最近色に入れない)。
   *
   * `onSpuitColor` (= 持続的な pen.color 反映、毎サンプル) と独立。色拾い自体は
   * `onSpuitColor` が従来どおり毎回反映し、本コールバックは「最近色に残す確定の1色」だけを
   * 担う。OOB クリック等で色が一度も拾えなかった操作では呼ばない (= 通知する色が無い)。
   *
   * 未指定なら no-op。Classic は渡さない (= 最近色機能を持たないので影響なし)。
   */
  readonly onSpuitCommit?: (rgb24: number) => void;
  /**
   * spuit-sample-modes-2026-06-22: スポイトのサンプル元モード (A/B/C)。
   * pick のたびに `engine.samplePixel(mode, layerId, x, y)` へ渡す。
   *   - A 'display' (既定) … 全可視レイヤー + bg (見たまま)、RGB のみ。
   *   - B 'layer'         … 選択レイヤー生ピクセル・bg 無視、α0 で消しゴム切替・
   *                         半透明 (1..255) は RGB のみ採用。
   *   - C 'layer-bg'      … 選択レイヤー + bg (旧 getLayerPixelColor 相当)、RGB のみ。
   * 未指定なら 'layer-bg' (= 旧来挙動の後方互換、test/legacy caller 向け)。共有 chrome の
   * `spuitSampleMode` を CanvasStage が流す。最新値は ref で hot-swap (effect 再 attach 回避)。
   */
  readonly spuitSampleMode?: SpuitSampleMode;
  /**
   * spuit-sample-modes-2026-06-22: モード B で α0 (= 完全透明) を拾ったとき呼ぶ。
   * パレット色は変えず、ツールを消しゴムへ切り替える想定 (caller = chrome.onSelectEraserDirect)。
   * 未指定なら no-op (= モード B α0 切替を使わない caller)。
   */
  readonly onSpuitEraser?: () => void;
  /**
   * 2026-05-09: 現選択ペン種に対応する筆圧 mapping 設定 (consult 2026-05-09 /
   * architecture.md §19.15). caller (`ClassicLayout`) が `pressureMap[pen.pen]`
   * を渡す. 未指定なら `DEFAULT_PEN_PRESSURE[pen.pen]` (全 ON, min=0.3) が使われる
   * (= 後方互換: pressure-aware でない caller でも従来通り `e.pressure` がそのまま
   * 載る挙動になる).
   *
   * **mapping 適用は送信側**: pointer event の `e.pressure` (∈ 0..1) に対し
   * `computeEffectivePressure(settings, e.pressure)` を通して effective `p` を
   * 算出、`stroke.segment.p` に乗せる. 受信側 rasterizer は user 設定を一切持たず、
   * 額面通りこの値を効果対象 (太さ・alpha) に反映する (= 二重 mapping 防止).
   */
  readonly pressureSettings?: PenPressureSettings;
  /**
   * 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09).
   *
   * `true` のとき `pointerType === 'pen'` 以外 (= mouse / touch) を `onPointerDown`
   * で early return する (= stroke / spuit / preview-drag いずれも開始しない).
   * `false` のときは全 pointer を受け付ける (= 既存挙動).
   *
   * 未指定なら `false` (= 後方互換、ペン専用モード未対応 caller でも従来通り動作).
   *
   * 状態は ClassicLayout 側の `useState<boolean>` で hold (session-local、
   * localStorage 不使用、ページ再読み込みでリセット).
   */
  readonly penOnlyMode?: boolean;
  /**
   * 2026-05-09 ペン専用モード自動 ON 復帰 callback (consult 2026-05-09).
   *
   * `pointerType === 'pen'` を検知したときに呼ばれる (= 毎回). caller (=
   * ClassicLayout) は `setPenOnlyMode(true)` を実行する. ユーザがマウスで
   * 一時的に OFF にしてもペンを再度使うたびに自動復帰する仕組み.
   *
   * StrictMode dev 二重実行 / 既に ON のときに余計な setState を避けたい場合は
   * caller 側で `if (!penOnlyMode)` 条件をかけて呼び出しを skip しても良い
   * (本 component は冪等に呼び出す).
   *
   * 未指定なら呼ばない (= ペン専用モード未対応 caller でも安全).
   */
  readonly onPenAutoEnable?: () => void;
  /**
   * penOnlyMode 中に単独の指タッチがジェスチャに昇格せず終わった (= 指を離した) とき
   * 呼ばれる (throttle / palm rejection 判定済み)。「何も描けない理由」の通知用。
   * pointerup 時のみで描画ホットパスには乗らない。未指定なら何もしない。
   */
  readonly onPenOnlyFingerIgnored?: () => void;
  /**
   * 表示専用リード線 (modern-layout.md §9.8.1 C 案) の publish 先。
   *
   * stroke ライフサイクルに合わせて「最後の確定 (平滑) 点」を publish する
   * ためだけの read-only sink。`LeadLineOverlay` が同 source を購読してリードを
   * 描く。**display-only**: ここでの publish は engine / transport 送信を一切
   * 変えない (リードは未コミット・未送信、§9.8.1 determinism)。
   *
   * - `startStroke` 後 … `begin()` (anchor をリセット + active 化)。
   * - filtered 点を emit するたび … その末尾 (最新平滑点) を `setAnchor()`。
   * - stroke 終了 … `end()` (active 解除)。
   *
   * 未指定なら no-op (リード線を使わない caller / 手ぶれ補正非対応経路)。
   *
   * **改訂 (§9.8.1 ①)**: SVG 細線は実ペンリードへ格上げされ、`LeadLineOverlay`
   * (SVG) は no-op 化された。この sink は後方互換のため残すが publish しても
   * SVG は描かない (実描画は `engine.setLead`)。
   */
  readonly leadLineSink?: LeadLineSource;
  /**
   * §9.8.1 ① 実ペンリードを出すか (= 手ぶれ補正 ON か)。`false` で完全 no-op
   * (`engine.setLead` を一切呼ばない / rAF も回さない)。Classic は常に false。
   *
   * 未指定なら false (= 後方互換、リード非対応 caller)。
   */
  readonly leadEnabled?: boolean;
  /**
   * §9.8.1 ① リードのなぞり方 (実装者の feel テスト用 flag)。
   *   - `'raw'`      … 生入力をなぞる (自分の線がそのまま先端に見える、第一候補)。
   *   - `'straight'` … anchor→カーソル直線 (生ジッタを見せない fallback)。
   * 未指定なら `'raw'`。
   */
  readonly leadPathMode?: LeadPathMode;
  /**
   * 画面座標 ⇔ canvas 座標の変換窓口 (canvas-rotation 段取り 1、`viewport.ts` §1b)。
   *
   * 表示が反転 / 拡大 / (将来) 回転していても、ストローク座標は**真の canvas 座標**で
   * 記録する必要がある。`toCanvasXY` / スポイトはポインタの `clientX/Y` をこの mapper で
   * 真の canvas 座標へ戻す (= 旧来の `getBoundingClientRect` 比率計算 + `width - x` の
   * flip 補正は廃止。回転すると rect が外接矩形に膨らみ比率計算が成り立たないため)。
   * 反転・回転は描画データに一切混入しない (= 他参加者・snapshot・protocol に無影響)。
   * 参照は mount 中不変 (値は呼び出しごとに最新を読む getter 方式) なので pointer 経路の
   * effect を再 attach させない。
   */
  readonly viewMapper: CanvasViewMapper;
  /**
   * 2026-06-13 (feature/preview-perf): 自分のストロークが進行中か を通知する callback。
   *
   * `pointerdown` で通常 stroke を開始した瞬間に `true`、`pointerup` / `pointercancel` /
   * `pointerleave` で確定した瞬間に `false` を呼ぶ (通常 stroke 経路のみ。spuit / imagepaste
   * -drag / blocked は対象外 = 描画ストロークではない)。
   *
   * 用途: プレビュー (Classic PreviewOverlay / Modern ModernPreviewPanel) が「自分の
   * ストローク中はサムネ再生成をサスペンドして、ブラシカーソルのフレーム予算を奪わない」
   * ようにするためのシグナル。表示専用で engine / transport を一切変えない。
   *
   * 未指定なら no-op (= 後方互換)。
   */
  readonly onStrokeActiveChange?: (active: boolean) => void;
  /**
   * 自分の通常ストロークを描き終えた (pointerup で stroke.end を送った) 瞬間に 1 回呼ぶ。
   * pointercancel 由来の確定は呼ばない。pen / width はペン尻の消しゴム化後の実効値。
   * 困りごとのコツ (domain/troubleTips.ts) の本数カウント用。表示専用で engine / transport は不変。
   */
  readonly onOwnStrokeEnd?: (info: {
    readonly pen: PenConfig['pen'];
    readonly width: number;
  }) => void;
  /**
   * 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md)。`true` の間は
   * 新しい stroke を始めない (pointerdown 入口 + 触り猶予窓明けの 2 箇所、どちらも
   * `beginStrokeNow` の前)。描いている最中に `true` になったら、その stroke はその場で
   * 終わらせる (エンジンには局所取り消しが無いのでローカルの 1 本は残る。復帰時の
   * snapshot で上書きされる)。スポイト / 画像プレビューのドラッグ / 2 本指ジェスチャは
   * 自分の画面で完結するので止めない。未指定は `false` (後方互換)。
   */
  readonly locked?: boolean;
  /** ロック中に描画しようとした (pointerdown) ときの合図。caller が帯を点滅させる。 */
  readonly onLockedPointerDown?: () => void;
  /**
   * 2026-06-20 canvas-background-color §8.4 透過確認チェッカー。
   *
   * `false` (= 背景を 👁 off にした個人ビュー) のとき canvas 要素へ `canvas-bg-checker`
   * class を付け、白い CSS 背景 (`--color-canvas`) を市松に差し替える。背景非可視時は
   * engine 背景ピクセルが透明 (alpha 0) になり、白 CSS 越しでは「白の上に透明＝白」で
   * 見た目が変わらないため、市松で「塗った白」と「未塗りの透明」を判別可能にする。
   *
   * `true` / 未指定なら class を付けず、代わりに `backgroundRgb` を CSS background-color
   * として canvas に当てる (下記)。表示専用で engine / transport を一切変えない
   * (capture/export は engine 経路で実 bg を焼くため CSS は出力非関与)。
   */
  readonly backgroundVisible?: boolean;
  /**
   * 2026-06-20 canvas-background-color CSS 方式 — 表示する背景色 (rgb24)。
   *
   * 表示合成から bg を外し canvas を **透明** に描く方針 (engine 変更) に伴い、未描画部の
   * 後ろに見える色は canvas 要素の CSS background が決める。`backgroundVisible !== false`
   * のとき、この rgb24 を `#rrggbb` に変換して inline `background-color` に当てる
   * (= `useRoomChrome.backgroundRgb` を反映、color 変更で worker/repaint なしに瞬時に
   * 変わる)。`backgroundVisible === false` のときはチェッカー class が優先されるため、
   * inline background は当てない (= 透過確認の市松を見せる)。
   *
   * 未指定なら白 (0xffffff、styles.css の `--color-canvas` と一致) に倒す (= 後方互換)。
   */
  readonly backgroundRgb?: number;
  /**
   * M1-c 2本指パン (modern-phone-shell-2026-07-02.md §3.1): 2本指ジェスチャ中の
   * 重心移動量 (client px) を通知する。caller (CanvasStage) は 2026-09-29 viewport
   * transform 移行 (決定5) 以降、`chrome.viewportRef.current` の `{tx,ty}` に
   * `dx/dy` をそのまま加算して `chrome.setViewport` へ渡す (= 指に内容が追従する
   * 方向。旧 `scrollLeft -= dx` の逆符号関係と違い直感的に加算で良い)。
   * 未指定なら 2本指パンは無効 (= 後方互換)。
   */
  readonly onGesturePan?: (dx: number, dy: number) => void;
  /**
   * M1-c ピンチズーム: 「ジェスチャ開始時 zoom × 2指距離比」の**絶対倍率**と、
   * アンカー (= 2指重心) の client 座標を通知する。caller (CanvasStage) は表示枠
   * 相対座標へ変換して `chrome.handleZoomToAtPointer` (M1-a の連続倍率アンカー
   * zoom API) を呼ぶ。clamp は API 側 (`clampZoom`) に任せる。
   * 未指定ならピンチズームは無効 (= 後方互換)。
   */
  readonly onGestureZoomTo?: (nextZoom: number, clientX: number, clientY: number) => void;
  /**
   * 2 本指のひねり (canvas-rotation 段取り 2): 「ジェスチャ開始時の回転 + しきい値を超えた
   * ひねり量」の**絶対回転角** (度、吸着済み、`[0,360)`) と回転軸 (= 2 本指重心) の client
   * 座標を通知する。しきい値 (`ROTATION_GESTURE_THRESHOLD_DEG`) を超えるまでは呼ばない
   * (= ピンチ・パン中の指のぶれで回らない)。ピンチ・パンと同じフレームで pan → zoom → rotate
   * の順に呼ぶ。開始回転は `viewMapper.getTransform().rotation` から読む。未指定ならひねり無効。
   */
  readonly onGestureRotateTo?: (nextRotation: number, clientX: number, clientY: number) => void;
  /**
   * タッチジェスチャ計測 (modern-phone-shell §7)。`started` = 2本指ジェスチャ入り
   * (1ジェスチャ1回、delayMs なし) / `late` = 猶予窓を逃して黙殺された2本目
   * (delayMs = 1本目→2本目 pointerdown の整数 ms、1ストローク1回)。fire-and-forget
   * (呼び側は例外を投げない)。hot path なので hot-swap ref 越しに読む。計測のみで
   * ジェスチャ挙動は変えない。未指定なら計測なし。
   */
  readonly onTouchGestureAnalytics?: (outcome: 'started' | 'late', delayMs?: number) => void;
  /**
   * 範囲の道具 (範囲の移動・コピー、docs/_wip/region-move-2026-10-15.md): 非 null の間は
   * 「ストロークを始める」代わりに、ドラッグを範囲選択 / 浮かせた絵の移動として `RegionPointerSink`
   * へ流す (座標は真の canvas 座標)。mouse / pen は押した瞬間、touch は他の touch と同じ猶予窓
   * (2 本指のパン / ピンチ / ひねりを生かす) を経てから始まる。スポイト・画像プレビューのドラッグが
   * 優先。**ペン専用モードでも touch (指) は通す** (範囲の道具の間だけ。palm rejection は
   * 範囲を選ぶ操作では要らない)。null / 未指定なら従来どおり描く。
   */
  readonly regionPointer?: RegionPointerSink | null;
}

// rgb24 (0..0xFFFFFF) → CSS `#rrggbb` (背景可視時に canvas へ当てる inline background-color)
// は `../shared/canvasBackgroundStyle.js` の `rgbToCss` へ寄せた (= プレビュー
// (PreviewOverlay / ModernPreviewPanel) と同じ変換を共有、feature/preview-panel-background
// 2026-09-28)。

/**
 * e2e / 自動化 (WebDriver) 時に描画エンジンを **inline path** (= OffscreenCanvas に
 * `transferControlToOffscreen` しない、main thread 描画) で起動させるかの判定。
 *
 * なぜ必要か: PR 2-β (2026-05-17) 以降、engine は常に `workerFactory` を渡され、
 * OffscreenCanvas 対応環境では canvas を worker へ transfer する。transfer 済みの
 * DOM canvas は `getContext('2d')` が `InvalidStateError` を throw するため、DOM の
 * `<canvas data-oekaki-canvas>` を直接 `getImageData` で読む e2e
 * (`snapshotResume.spec.ts` の `sampleAlpha`) がアサーション到達前に落ちる。
 * inline path なら DOM canvas がそのまま main canvas になり (§19.6, 本 file 冒頭 §16
 * コメント)、getImageData で表示ピクセルを読める。描画結果は inline / offscreen で
 * bit-identical (本 file 46 行目コメント「falls back to inline (= identical…)」) なので、
 * この切替はテストの読み取り経路だけを変え、絵そのものは変わらない。
 *
 * 判定は `navigator.webdriver`: Playwright / WebDriver 等の自動化下でのみ `true`。
 * 実ユーザーのブラウザでは常に `false` なので、本番 / 実ユーザーの OffscreenCanvas
 * Worker 経路は一切変わらない (= デフォルト OFF)。加えて `VITE_OEKAKI_FORCE_INLINE=1`
 * を明示 override として尊重する (= 開発者が手元で inline path を検証したいとき用、
 * 未設定なら無効)。
 */
const shouldForceInlineDraw = (): boolean => {
  if (import.meta.env.VITE_OEKAKI_FORCE_INLINE === '1') return true;
  // 描き味ベンチ専用 override (e2e/bench/drawingLatency.bench.ts): webdriver 下でも
  // 本番と同じ OffscreenCanvas Worker 経路で描かせる。`import.meta.env.DEV` ガードなので
  // production bundle では丸ごと消える (= 本番の挙動・判定は不変)。
  if (
    import.meta.env.DEV &&
    (globalThis as { __OEKAKI_FORCE_WORKER__?: unknown }).__OEKAKI_FORCE_WORKER__ === true
  ) {
    return false;
  }
  return typeof navigator !== 'undefined' && navigator.webdriver === true;
};

/**
 * Carries the kind of in-progress gesture across pointer events. Without
 * this we'd have to re-check the engine on every pointermove (racy if the
 * preview state flips mid-drag).
 */
type ActivePointer =
  | { readonly kind: 'stroke'; readonly stroke: ActiveStroke; readonly pointer: number }
  | { readonly kind: 'imagepaste-drag'; readonly pointer: number }
  /** 範囲の道具のドラッグ (範囲選択 / 浮かせた絵の移動)。`regionPointer` に流す。 */
  | { readonly kind: 'region-drag'; readonly pointer: number }
  /**
   * `'blocked'`: pointerdown landed while a preview was active but the
   * click was outside the preview rect. We swallow subsequent move/up so
   * a stroke isn't accidentally started underneath the overlay.
   */
  | { readonly kind: 'blocked'; readonly pointer: number };

interface ActiveStroke {
  sid: string;
  pending: StrokePoint[];
  rafHandle: number | null;
  /** 実効ペン種 / 太さ (onOwnStrokeEnd 用)。 */
  pen: PenConfig['pen'];
  width: number;
}

/**
 * PR 2-β (2026-05-17) — module-local WeakMaps that survive React
 * StrictMode dev double-mount. The OffscreenCanvas `transferControlToOffscreen`
 * spec is one-shot per canvas; the only way to keep worker mode alive
 * across StrictMode's mount → cleanup → mount cycle is to (a) defer the
 * dispose and (b) recover the existing engine on the immediate re-mount.
 *
 * WeakMap so canvases can still be GC'd when truly unmounted (the
 * `setTimeout` body holds a strong ref to its captured `canvas` for the
 * delay window, but the WeakMap entries don't pin anything beyond that).
 *
 * `MOUNT_REUSE_DELAY_MS` — how long we hold off the actual dispose to
 * let StrictMode re-mount catch up. React StrictMode's double-invoke is
 * synchronous-ish so even 0 would usually work, but 100ms gives a
 * comfortable margin against HMR / async hydration edge cases without
 * delaying real teardown perceptibly.
 */
const engineByCanvas = new WeakMap<HTMLCanvasElement, CanvasDrawingEngine>();
const pendingDisposeByCanvas = new WeakMap<HTMLCanvasElement, ReturnType<typeof setTimeout>>();
const MOUNT_REUSE_DELAY_MS = 100;

/**
 * エアブラシ hold-to-build (airbrush-design-2026-08-15.md §3): 押しっぱなしで
 * 濃くなる機能。`pen==='airbrush'` のときだけ有効 (§3 適用範囲)。
 *
 * - `AIRBRUSH_HOLD_TICK_INTERVAL_MS`: pointermove が途切れたと判定するしきい値
 *   (§3.4「pointermove が 50ms 途切れたら現在座標を注入」= 最低点レートの保証)。
 *   常時定期送出ではない — 実移動中の pointermove のたびにこのタイマーを
 *   clear+reschedule する (debounce) ので、動いている間は 1 回も発火しない。
 * - `AIRBRUSH_HOLD_TICK_MAX_STREAK`: 同一座標の連続 tick の上限 (§3.5、濃度飽和 +
 *   押しっぱなしで真っ黒になる事故防止 + 点数爆発の抑制)。50ms 間隔 × 30 = 最大 1.5
 *   秒ぶんの静止 tick で頭打ち (それ以降は実移動があるまで無送信)。
 *
 * 両定数とも設計書 §9 に「実機で確定」と明記された調整項目 (要検証)。ここでは
 * 「静止直後から連続的にビルドアップし、かつ数秒握りっぱなしで無限に濃くならない」
 * バランスの初期値として置く。
 */
const AIRBRUSH_HOLD_TICK_INTERVAL_MS = 50;
const AIRBRUSH_HOLD_TICK_MAX_STREAK = 30;

/**
 * `PointerEvent.pointerType` は仕様上 `string` (= 将来の拡張に備えた open enum:
 * `'mouse' | 'pen' | 'touch' | ''` など). `computeEffectivePressure` は厳密に
 * `'mouse' | 'pen' | 'touch' | undefined` を要求するので narrow する.
 *
 * 未知値 (= `''` や将来の新規値) は `undefined` に倒し、既存の「mouse/touch
 * 相当の保守的扱い (= 0/1 omit)」で挙動を維持する (= 安全側 default).
 */
const narrowPointerType = (raw: string): 'mouse' | 'pen' | 'touch' | undefined => {
  if (raw === 'mouse' || raw === 'pen' || raw === 'touch') return raw;
  return undefined;
};

export const DrawingCanvas = ({
  width,
  height,
  pen,
  transport,
  onEngineReady,
  canvasRef: externalCanvasRef,
  onSpuitColor,
  spuitMode,
  onSpuitCommit,
  spuitSampleMode,
  onSpuitEraser,
  pressureSettings,
  penOnlyMode,
  onPenAutoEnable,
  leadLineSink,
  leadEnabled,
  leadPathMode,
  viewMapper,
  onStrokeActiveChange,
  onOwnStrokeEnd,
  locked = false,
  onLockedPointerDown,
  backgroundVisible,
  backgroundRgb,
  onPenOnlyFingerIgnored,
  onGesturePan,
  onGestureZoomTo,
  onGestureRotateTo,
  onTouchGestureAnalytics,
  regionPointer = null,
}: DrawingCanvasProps): React.ReactNode => {
  const internalCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<CanvasDrawingEngine | null>(null);
  const activeRef = useRef<ActivePointer | null>(null);
  /**
   * 2026-05-06 追加 — ドラッグ中の連続スポイト用 state (Photoshop 踏襲).
   * pointerdown で spuit ジェスチャ確定 → ON、pointerup/cancel/leave → OFF。
   * pointermove でこの ref が ON なら毎 move で `getLayerPixelColor` を
   * 呼んで `onSpuitColor` に流す. ON 中は `activeRef` は常に null
   * (= pointerdown で stroke 開始を skip しているため stroke 系の早期 return
   * と相互排他になる).
   *
   * `useState` ではなく `useRef` にしている理由:
   *   - re-render を起こす必要がない (DOM やコンポーネント tree に出さない)
   *   - pointermove 経路は高頻度で hot path、毎回 setState を走らせると
   *     描画系と競合する可能性がある
   *   - pointerId も合わせて持って multi-touch 時の異 pointer 干渉を防ぐ
   */
  const spuitDragRef = useRef<{ pointer: number } | null>(null);
  // 2026-06-20 (`fix/spuit-pen-rightclick`): 右クリックを pointerdown で即ピックするように
  // したので、直後に来る `contextmenu` (マウス右クリックは必ず発火) の初回ピックと二重に
  // ならないようガードする。pointerdown でピックした pointerId をここに記録し、`onContextMenu`
  // は最新の pointerdown ピックがあれば自前のピックを skip する。pointer 1 操作ぶんの寿命で、
  // finish (pointerup/cancel/leave) でクリアする。null = 直近 pointerdown でピックしていない。
  const spuitPickedOnPointerDownRef = useRef<number | null>(null);
  // Latest pen config without forcing the effect below to re-run on every change.
  const penRef = useRef(pen);
  penRef.current = pen;
  // 同上: spuit callback も最新を見せる (= effect の再実行を避ける).
  const onSpuitColorRef = useRef(onSpuitColor);
  onSpuitColorRef.current = onSpuitColor;
  // 2026-06-12 スポイト確定シグナル (最近色厳密化): commit callback も hot-swap ref。
  const onSpuitCommitRef = useRef(onSpuitCommit);
  onSpuitCommitRef.current = onSpuitCommit;
  // spuit-sample-modes-2026-06-22: サンプル元モード + α0 消しゴム切替 callback も hot-swap ref。
  // pointer 経路 (effect closure) は最新モードを request 時に読む。未指定は 'layer-bg' (= 旧挙動)。
  const spuitSampleModeRef = useRef<SpuitSampleMode>(spuitSampleMode ?? 'layer-bg');
  spuitSampleModeRef.current = spuitSampleMode ?? 'layer-bg';
  const onSpuitEraserRef = useRef(onSpuitEraser);
  onSpuitEraserRef.current = onSpuitEraser;
  // 2026-06-13 スポイトドラッグの色追従コアレッシング (feature/spuit-drag-realtime):
  // ドラッグ中の連続 pick を「in-flight 1個 + seq ガード」で捌くステートマシン
  // (`SpuitPickCoalescer`)。worker 往復の順不同/遅延 resolve による色追従ズレを解消する。
  // pick 結果 (onColor) を `pen.color` に反映 (= onSpuitColor)、操作確定 (onCommit) を
  // 最近色記録 (= onSpuitCommit) に流す。callback は hot-swap ref を経由して最新を読む
  // ので、coalescer 自体は engine と紐づけて1度だけ生成する (engine 差し替え時に張り直す)。
  // engine がまだ無い / spuit callback 未指定なら null。
  const spuitCoalescerRef = useRef<SpuitPickCoalescer | null>(null);
  // 2026-05-09: 筆圧 mapping 設定も同様に ref で hot-swap.
  // - prop 未指定なら現在のペン種のデフォルトに倒す (= pressure-aware でない
  //   caller との後方互換). penRef.current.pen で都度デフォルトを引き直す.
  const pressureSettingsRef = useRef<PenPressureSettings | undefined>(pressureSettings);
  pressureSettingsRef.current = pressureSettings;
  // 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09):
  // - penOnlyMode (read) と onPenAutoEnable (= setPenOnlyMode(true) を呼ぶ
  //   callback) を ref 化. pointerdown ハンドラは useEffect 内 closure で参照
  //   するため、prop が変わっても useEffect を再実行させずに最新値を読みたい.
  // - 未指定なら penOnlyMode=false (= 既存挙動と同じく全 pointer を通す), 自動
  //   ON callback も no-op.
  const penOnlyModeRef = useRef<boolean>(penOnlyMode ?? false);
  penOnlyModeRef.current = penOnlyMode ?? false;
  const onPenAutoEnableRef = useRef<(() => void) | undefined>(onPenAutoEnable);
  onPenAutoEnableRef.current = onPenAutoEnable;
  // §9.8.1 表示専用リード線の publish 先。pointer 経路は高頻度なので ref で
  // hot-swap し、effect を再実行させずに最新を読む (penRef 等と同流儀)。
  const leadLineSinkRef = useRef<LeadLineSource | undefined>(leadLineSink);
  leadLineSinkRef.current = leadLineSink;
  // §9.8.1 ① 実ペンリード state (hot path なので ref、effect 再実行を避ける)。
  const leadEnabledRef = useRef<boolean>(leadEnabled ?? false);
  leadEnabledRef.current = leadEnabled ?? false;
  const leadPathModeRef = useRef<LeadPathMode>(leadPathMode ?? 'raw');
  leadPathModeRef.current = leadPathMode ?? 'raw';
  // 最後に確定 (emit) した平滑点 (canvas native px + 筆圧軸) = リード始点 (anchor)。
  // 筆圧を載せるのは、リード区間 (anchor→cursor) を確定線と同じ筆圧→幅で描くため
  // (= 確定線の終端幅から連続させる)。欠けると先端が最大幅になる (2026-06-27 fix)。
  const leadAnchorRef = useRef<{ x: number; y: number; p?: number; pAlpha?: number } | null>(null);
  // anchor 以降に到着した生入力列 (canvas px)。emit 後にクリアし、次 flush までの
  // 「確定平滑フロンティア〜カーソル」をなぞる材料 ('raw' モード用)。
  const leadRawTailRef = useRef<LeadSamplePoint[]>([]);
  // 最新の生カーソル (canvas px + 筆圧軸) = リード終点。
  const leadCursorRef = useRef<LeadSamplePoint | null>(null);
  // 現 active stroke のペン見え (色/太さ/濃さ/AA/layer/mask)。setLead に渡す。
  const leadPenRef = useRef<{
    pen: PenConfig['pen'];
    color: number;
    alpha: number;
    width: number;
    aa: boolean;
    layerId: number;
    mask: PenConfig['mask'];
    // texture preset for the live lead preview (texture-brush-design.md §11
    // step 3). undefined for non-texture pens → 紙 default / ignored.
    penParams: ReturnType<typeof penParamsFor>;
  } | null>(null);
  // リード描画 rAF ハンドル (stroke 中だけ回す)。
  const leadRafRef = useRef<number | null>(null);
  // 画面 ⇔ canvas 座標の変換窓口。pointer plumbing の useEffect は
  // `[transport]` 依存なので、再 attach させないよう ref で最新値を読む
  // (penOnlyMode 等と同じ hot-swap パターン)。
  const viewMapperRef = useRef<CanvasViewMapper>(viewMapper);
  viewMapperRef.current = viewMapper;
  // §9.6 スポイト持続モード (Modern 専用): pointerdown ハンドラは useEffect 内 closure で
  // 参照するため、prop が変わっても useEffect を再 attach させずに最新値を読む
  // (penOnlyMode / flipX 等と同じ hot-swap パターン)。未指定なら false (= Classic 無影響)。
  const spuitModeRef = useRef<boolean>(spuitMode ?? false);
  spuitModeRef.current = spuitMode ?? false;
  // 範囲の道具: pointer 経路 (effect closure) が最新の窓口を読む hot-swap ref。
  const regionPointerRef = useRef<RegionPointerSink | null>(regionPointer);
  regionPointerRef.current = regionPointer;
  // 2026-06-13 (feature/preview-perf): ストローク進行中シグナルの通知先。pointer 経路は
  // 高頻度 (effect 再 attach を避けたい) なので ref で hot-swap し最新を読む。
  const onStrokeActiveChangeRef = useRef<((active: boolean) => void) | undefined>(
    onStrokeActiveChange,
  );
  onStrokeActiveChangeRef.current = onStrokeActiveChange;
  const onOwnStrokeEndRef = useRef(onOwnStrokeEnd);
  onOwnStrokeEndRef.current = onOwnStrokeEnd;
  // 再接続中は描けない (docs/_wip/reconnect-draw-lock-2026-10-12.md): pointer 経路は
  // effect を再 attach させたくないので ref で hot-swap し最新を読む。
  const lockedRef = useRef<boolean>(locked);
  lockedRef.current = locked;
  const onLockedPointerDownRef = useRef<(() => void) | undefined>(onLockedPointerDown);
  onLockedPointerDownRef.current = onLockedPointerDown;
  // 描画中にロックされたとき、その stroke をその場で終わらせるための窓口
  // (pointer effect 内の `finish` を指す。effect の cleanup で null に戻す)。
  const forceEndStrokeRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (locked) forceEndStrokeRef.current?.();
  }, [locked]);
  // M1-c タッチジェスチャ (猶予窓 + 2本指パン/ピンチ、modern-phone-shell §2 確定B案):
  // 状態機械のフェーズ / 猶予窓バッファ / ジェスチャ session。いずれも pointer 経路
  // (hot path) から読み書きするので ref。遷移判定は pure reducer (`touchGesture.ts`)
  // に委ね、ここは reducer の返す effects を実行する impure shell の状態だけを持つ。
  const touchPhaseRef = useRef<TouchGesturePhase>(IDLE_TOUCH_PHASE);
  const touchPendingRef = useRef<{
    readonly pointerId: number;
    /** pointerdown 時点のペン設定 snapshot (= 窓中の設定変更を混ぜない)。 */
    readonly pen: PenConfig;
    /** 窓中に溜めた点列 (canvas 座標)。ストローク開始時にそのまま流す。 */
    readonly pts: StrokePoint[];
    /** 1本目の最新 client 座標 (= ジェスチャ入り時の初期位置に使う)。 */
    lastClient: GesturePoint;
    timer: number | null;
  } | null>(null);
  const touchGestureRef = useRef<{
    /** 追跡中の2指の最新 client 座標 (第3指以降は追跡しない = 黙殺)。 */
    readonly pointers: Map<number, GesturePoint>;
    readonly startZoom: number;
    readonly startDistance: number;
    /** ひねりトラッカー (しきい値 + 吸着)。回転が無効なら null。 */
    readonly rotation: RotationTracker | null;
    lastCentroid: GesturePoint;
    rafHandle: number | null;
  } | null>(null);
  /**
   * 決定1 (consult 2026-09-29、modern-phone-shell-2026-07-02.md §6): `touchGestureRef`
   * の現在のセッションが「非 penOnly の猶予窓 (`'window'`)」と「penOnlyMode 中の
   * touch (`'pen-only'`、`transitionPenOnlyTouch`)」のどちらが開いたものかを覚えて
   * おく。2本指の move/finish 追跡自体 (`enterGesture` / `applyGestureFrame` /
   * `onGestureDocPointerMove`) は両者で完全共用するが、finish 時にどちらの reducer
   * へ dispatch するかだけがこのタグで分岐する (`onGestureDocPointerFinish`)。
   */
  const gestureOwnerRef = useRef<'window' | 'pen-only'>('window');
  // ジェスチャ callback も hot-swap ref (penRef 等と同流儀、effect 再 attach 回避)。
  const onGesturePanRef = useRef(onGesturePan);
  onGesturePanRef.current = onGesturePan;
  const onGestureZoomToRef = useRef(onGestureZoomTo);
  onGestureZoomToRef.current = onGestureZoomTo;
  const onGestureRotateToRef = useRef(onGestureRotateTo);
  onGestureRotateToRef.current = onGestureRotateTo;
  const onTouchGestureAnalyticsRef = useRef(onTouchGestureAnalytics);
  onTouchGestureAnalyticsRef.current = onTouchGestureAnalytics;
  // 計測 (§7): 非 penOnly の touch 1本目 pointerdown の記録 (late 判定用)。
  const touchFirstDownRef = useRef<TouchFirstDown | null>(null);
  // 決定1 (§6): penOnlyMode 中の touch 専用状態機械 (`transitionPenOnlyTouch`) の
  // フェーズ。上の `touchPhaseRef` (非 penOnly の猶予窓) とは独立 (= penOnlyMode
  // ON/OFF どちらか一方しか使わないが、hot-swap 中の切替を安全にするため常に両方
  // idle 初期化しておく)。
  const penOnlyTouchPhaseRef = useRef<PenOnlyTouchPhase>(IDLE_PEN_ONLY_TOUCH_PHASE);
  /**
   * 決定1 (§6): 「pen が接地中か」(= `pointerType === 'pen'` の pointerdown 〜
   * pointerup/cancel/leave)。penOnlyMode 中の touch 判定でだけ参照する
   * (= penOnlyMode OFF の既存経路には一切影響しない)。pen 自体は penOnlyMode の
   * ON/OFF に関わらず常時ここを更新する (= 「pen が今まさに描いているか」を素直に
   * 追う、penOnlyMode 側の消費だけを条件付きにする)。
   */
  const penActiveRef = useRef<boolean>(false);
  // 指無視通知 (feature/pen-only-finger-notice): pen-up 時刻 (手のひら置き窓) と最終通知時刻。
  const lastPenUpAtRef = useRef<number | null>(null);
  const lastFingerNoticeAtRef = useRef<number | null>(null);
  const onPenOnlyFingerIgnoredRef = useRef(onPenOnlyFingerIgnored);
  onPenOnlyFingerIgnoredRef.current = onPenOnlyFingerIgnored;

  // エアブラシ hold-to-build (airbrush-design-2026-08-15.md §3): アクティブな
  // airbrush stroke だけが持つ状態。`beginStrokeNow` で `effectivePen.pen ===
  // 'airbrush'` のときだけ生成され、他ペンでは常に null (= §3 適用範囲、他ペンの
  // 挙動に一切触れない)。
  //
  // - `lastPoint`: 直近の実 pointermove 点 (筆圧含む)。tick はこの座標・筆圧を
  //   踏襲する (§4 の「tick 点が運ぶ筆圧は直前の点の値を踏襲」)。
  // - `streakCoord` / `streakCount`: 直近 tick の (x,y) と、そこから続く同一座標
  //   tick の連投数 (§3.5 N cap の「座標のみで同一判定」)。実移動で座標が変わった
  //   tick が発火した瞬間にリセットされる (= 判定は tick 発火時に行う、pointermove
  //   のたびにリセットする必要はない)。
  const holdTickRef = useRef<{
    readonly sid: string;
    timer: number | null;
    lastPoint: StrokePoint;
    streakCoord: { x: number; y: number };
    streakCount: number;
  } | null>(null);

  /**
   * Set both the internal ref (for our handlers) and the external ref
   * (for sibling overlays). Single callback ref because React refuses to
   * accept two `ref={...}` props on one element.
   */
  const setCanvasRef = (el: HTMLCanvasElement | null): void => {
    internalCanvasRef.current = el;
    if (externalCanvasRef !== undefined) {
      externalCanvasRef.current = el;
    }
  };

  // Construct / dispose the engine. drawing-engine-developer は CanvasDrawingEngine の
  // 中身を差し替えるが、ここでの構築タイミング (canvas mount 直後) は変えない。
  //
  // PR 2-β (2026-05-17) — `workerFactory` を渡して OffscreenCanvas Worker 経路を
  // active 化. Vite `?worker` import で worker code が main bundle と分離され、
  // 各 engine instance が自前の Worker を持つ (canvas dispose = worker terminate).
  // 旧来の inline path はそのまま fallback として残るので、Safari iOS 16.4 未満
  // (= OffscreenCanvas / transferControlToOffscreen 未対応) は engine が
  // `canSupportOffscreenWorker` で false 判定して inline で起動する.
  //
  // **StrictMode dev double-mount 対応** (PR 2-β fix, 2026-05-17):
  //   OffscreenCanvas の `transferControlToOffscreen` は spec 上 1 度きり
  //   呼べる. React StrictMode の dev double-invoke (mount → cleanup →
  //   mount) では 2 回目 mount で同じ canvas に対し 2 度目の transfer が
  //   発生して `InvalidStateError`. inline path も既に「transferred」状態
  //   の canvas には `getContext('2d')` が throw する.
  //
  //   救済: dispose を `MOUNT_REUSE_DELAY_MS` 遅延し、その間に新規 mount
  //   が来たら **同じ engine を resurrect** (= dispose timer を cancel).
  //   ホスト canvas を WeakMap で keying するので「同じ canvas に対する
  //   double mount」のみ救済する (= 違う canvas は普通に新 engine).
  //
  //   prod build では StrictMode の double-invoke は無いので timer は
  //   常に発火 (= 即 dispose、worker terminate). dev だけが timer cancel
  //   経路を踏む.
  useEffect(() => {
    const canvas = internalCanvasRef.current;
    if (canvas === null) return;
    let engine = engineByCanvas.get(canvas);
    const pendingTimer = pendingDisposeByCanvas.get(canvas);
    if (engine !== undefined && pendingTimer !== undefined) {
      // Resurrect: cleanup queued a dispose; cancel it and reuse the engine.
      clearTimeout(pendingTimer);
      pendingDisposeByCanvas.delete(canvas);
    } else {
      engine = new CanvasDrawingEngine(canvas, {
        workerFactory: () => new DrawingWorkerCtor(),
        // e2e / 自動化時だけ inline 描画に落とす (= DOM canvas を transfer せず、
        // e2e の `getImageData` 読み取りを可能にする)。実ユーザーは常に false =
        // OffscreenCanvas Worker 経路そのまま (`shouldForceInlineDraw` 参照)。
        forceInline: shouldForceInlineDraw(),
        // ドットバイドット表示: 端末の保存値で初期化 (切替は chrome が setDotByDotMode)。
        dotByDotMode: createDotByDotPreferenceStore().get(),
      });
      engineByCanvas.set(canvas, engine);
    }
    engineRef.current = engine;
    onEngineReady?.(engine);
    return () => {
      // Notify subscribers BEFORE the (eventual) dispose so they stop
      // dispatching into a half-torn-down engine.
      onEngineReady?.(null);
      engineRef.current = null;
      // Capture the local for the timer body so re-mount can't see stale
      // state via closure.
      const liveEngine = engine;
      const timer = setTimeout(() => {
        pendingDisposeByCanvas.delete(canvas);
        engineByCanvas.delete(canvas);
        liveEngine?.dispose();
      }, MOUNT_REUSE_DELAY_MS);
      pendingDisposeByCanvas.set(canvas, timer);
    };
  }, [onEngineReady]);

  // Keep the engine's internal buffer size in sync with the prop.
  useEffect(() => {
    engineRef.current?.resize(width, height);
  }, [width, height]);

  // Pointer plumbing.
  useEffect(() => {
    const canvas = internalCanvasRef.current;
    if (canvas === null) return;

    /**
     * 2026-05-09 (consult): 描画 stroke (通常 stroke / spuit-drag /
     * imagepaste-drag) 開始時に「全要素 select 抑止」のグローバル CSS フラグを
     * root の `data-drawing-active` に attach する. これと一緒に既存選択 range も
     * クリア (= ボタン操作で残った選択が描画開始を妨げないように).
     *
     * Step 1 (個別 `select-none`) で UI ラベルは塞いだが、Pencil ジェスチャの
     * 行き場が ChatPanel メッセージ履歴 (= コピペ要件で `select-text` 維持)
     * に集中する現象が残ったための対処. グローバル CSS は `styles.css` の
     * `[data-drawing-active="true"] *` ルール参照.
     */
    const beginSuppressSelection = (): void => {
      window.getSelection()?.removeAllRanges();
      document.documentElement.dataset.drawingActive = 'true';
    };
    const endSuppressSelection = (): void => {
      delete document.documentElement.dataset.drawingActive;
    };

    const flushPending = (): void => {
      const active = activeRef.current;
      if (active === null || active.kind !== 'stroke') return;
      active.stroke.rafHandle = null;
      if (active.stroke.pending.length === 0) return;
      const engine = engineRef.current;
      // §9.8: the transport must carry the SAME (stabilised) points the engine
      // rendered, not the raw input, so peers + the server snapshot stay on
      // one identical curve. `addPoints` returns the filtered points; with the
      // identity filter (stabiliser off, the default) this == the input, so
      // off-level behaviour is bit-identical. When the engine isn't ready yet
      // we fall back to the raw pending (no filter available = identity).
      const toSend =
        engine !== null
          ? engine.addPoints(active.stroke.sid, active.stroke.pending)
          : active.stroke.pending;
      if (toSend.length > 0) {
        sendStrokeSegment(transport, { sid: active.stroke.sid, pts: toSend });
      }
      // §9.8.1 — advance the lead anchor to the latest stabilised (emitted)
      // point. Display-only: this does NOT touch `toSend` / the transport
      // (the lead is never sent or committed). With the identity filter
      // `toSend` == raw input, but `setLead` is only driven when stabilisation
      // is on (`leadEnabledRef`), so this is a harmless no-op for Classic /
      // stabiliser-off.
      publishLeadAnchor(toSend);
      active.stroke.pending = [];
    };

    /**
     * §9.8.1 — advance the lead anchor to the last point of an emitted
     * (filtered) batch (= 確定平滑フロンティア前進). Read-only over `pts`;
     * never mutates or re-sends.
     *
     * Also resets the raw-tail accumulator: every raw sample up to this flush
     * has now been consumed by the stabiliser, so the gap「frontier → cursor」
     * restarts empty and rebuilds from subsequent pointermove samples. This
     * keeps the lead's raw path short (= 隙間ぶんだけ, 負荷2倍回避).
     *
     * Still publishes to the (now display-no-op) SVG sink for backward compat.
     */
    const publishLeadAnchor = (pts: readonly StrokePoint[]): void => {
      const last = pts[pts.length - 1];
      if (last === undefined) return;
      leadLineSinkRef.current?.setAnchor(last.x, last.y);
      if (!leadEnabledRef.current) return;
      // Carry the emitted point's pressure into the anchor so the lead segment
      // (anchor→cursor) is rasterized at the SAME pressure→width as the body it
      // continues from (= 先端が最大幅にならない, 2026-06-27 fix).
      leadAnchorRef.current = {
        x: last.x,
        y: last.y,
        ...(last.p !== undefined ? { p: last.p } : {}),
        ...(last.pAlpha !== undefined ? { pAlpha: last.pAlpha } : {}),
      };
      // The stabiliser has caught up to this point; restart the raw tail.
      leadRawTailRef.current = [];
    };

    /**
     * §9.8.1 ① — drive the real-pen lead each animation frame while a stroke
     * is active and stabilisation is on. Builds the canvas-space gap point
     * list (anchor → raw tail → cursor, or anchor → cursor straight) and hands
     * it to `engine.setLead`, which rasterizes it with the SAME stamp machinery
     * the committed body uses (display-only, never sent / committed).
     *
     * Bounded cost: the point list is the short gap only (数〜十数点); the
     * smoothed body is never re-rasterized. When the gap collapses (= 補正 OFF
     * 相当 / ラグ無し) the list is empty → `clearLead`.
     */
    const renderLead = (): void => {
      leadRafRef.current = window.requestAnimationFrame(renderLead);
      const engine = engineRef.current;
      const penMeta = leadPenRef.current;
      if (engine === null || penMeta === null) return;
      const pts = computeLeadSegmentPoints(
        leadAnchorRef.current,
        leadRawTailRef.current,
        leadCursorRef.current,
        leadPathModeRef.current,
      );
      if (pts.length === 0) {
        engine.clearLead();
        return;
      }
      engine.setLead({
        pen: {
          pen: penMeta.pen,
          color: penMeta.color,
          alpha: penMeta.alpha,
          width: penMeta.width,
          aa: penMeta.aa,
          // texture preset for the lead (texture-brush-design.md §11 step 3):
          // the lead uses the SAME sampler as the committed body.
          ...(penMeta.penParams !== undefined ? { penParams: penMeta.penParams } : {}),
        },
        layerId: penMeta.layerId,
        mask: penMeta.mask,
        points: pts,
      });
    };

    const startLeadLoop = (): void => {
      if (!leadEnabledRef.current) return;
      if (leadRafRef.current !== null) return;
      leadRafRef.current = window.requestAnimationFrame(renderLead);
    };

    /** Convert a wire `StrokePoint` to a lead sample (drop undefined pressure axes). */
    const leadSampleOf = (pt: StrokePoint): LeadSamplePoint => {
      if (pt.p !== undefined && pt.pAlpha !== undefined) {
        return { x: pt.x, y: pt.y, p: pt.p, pAlpha: pt.pAlpha };
      }
      if (pt.p !== undefined) return { x: pt.x, y: pt.y, p: pt.p };
      if (pt.pAlpha !== undefined) return { x: pt.x, y: pt.y, pAlpha: pt.pAlpha };
      return { x: pt.x, y: pt.y };
    };

    const stopLeadLoop = (): void => {
      if (leadRafRef.current !== null) {
        window.cancelAnimationFrame(leadRafRef.current);
        leadRafRef.current = null;
      }
      // Reset lead state + wipe any visible lead. `clearLead` is idempotent.
      leadAnchorRef.current = null;
      leadRawTailRef.current = [];
      leadCursorRef.current = null;
      leadPenRef.current = null;
      engineRef.current?.clearLead();
    };

    const scheduleFlush = (): void => {
      const active = activeRef.current;
      if (active === null || active.kind !== 'stroke') return;
      if (active.stroke.rafHandle !== null) return;
      active.stroke.rafHandle = window.requestAnimationFrame(flushPending);
    };

    // ─── エアブラシ hold-to-build (airbrush-design-2026-08-15.md §3) ──────────
    //
    // 【最重要】tick 点は `pointFilter.push` を一切通さない
    // (`engine.injectHoldTickPoint` 経由、`CanvasDrawingEngine.ts` のコメント
    // 参照)。§3.3: 手ぶれ補正 (weak/medium/strong) はゼロ長セグメントを無条件で
    // 捨てるため、素朴に addPoints 経路へ流すと手ぶれ補正 ON のユーザーで
    // hold-to-build が全滅する。
    //
    // ローカル描画に使う点列と wire に流す点列は同一になる
    // (`injectHoldTickPoint` が返す点をそのまま `sendStrokeSegment` へ渡す、
    // §1.2 の決定論の前提)。

    /** hold-tick タイマーを停止し状態を破棄する。stroke 終了 / pen 切替で呼ぶ。 */
    const stopHoldTick = (): void => {
      const state = holdTickRef.current;
      if (state !== null && state.timer !== null) window.clearTimeout(state.timer);
      holdTickRef.current = null;
    };

    /**
     * 50ms の pointermove 途切れ検出タイマーが発火したときの本体 (§3.4)。
     * 現在座標 (直近の実点) を 1 tick 注入し、cap 未到達ならタイマーを再スケジュール
     * して次の静止 50ms でまた発火できるようにする (= 連続的にビルドアップ、§3.2)。
     * cap 到達時は再スケジュールしない — 次に目を覚ますのは実 pointermove による
     * リスケジュール (= 座標が変わった) のみ (§3.5)。
     */
    const fireHoldTick = (): void => {
      const state = holdTickRef.current;
      if (state === null) return;
      const pt = state.lastPoint;
      // 同一判定は座標のみ (§3.5): 筆圧は静止中も揺らぐため、点全体を比較すると
      // cap が効かない。座標が変わっていれば新しい streak として数え直す。
      if (pt.x !== state.streakCoord.x || pt.y !== state.streakCoord.y) {
        state.streakCoord = { x: pt.x, y: pt.y };
        state.streakCount = 0;
      }
      if (state.streakCount < AIRBRUSH_HOLD_TICK_MAX_STREAK) {
        state.streakCount += 1;
        const engine = engineRef.current;
        if (engine !== null) {
          const injected = engine.injectHoldTickPoint(state.sid, pt);
          if (injected.length > 0) {
            sendStrokeSegment(transport, { sid: state.sid, pts: injected });
          }
        }
        state.timer = window.setTimeout(fireHoldTick, AIRBRUSH_HOLD_TICK_INTERVAL_MS);
      } else {
        // cap 到達: 再スケジュールしない。次の実 pointermove が来て座標が変わる
        // まで tick を止める (= 押しっぱなしで無限に濃くならない、§3.5)。
        state.timer = null;
      }
    };

    /**
     * 実 pointermove ごとに呼ぶ (= debounce のリセット)。airbrush stroke 中でなければ
     * no-op。`beginStrokeNow` が生成した state の `lastPoint` を最新の実点で更新し、
     * 保留中のタイマーを取り消して 50ms からやり直す — これにより「動いている間は
     * 1 回も tick が発火しない」(§3.4「常時 20Hz tick ではない」) を保証する。
     */
    const noteHoldTickRealPoint = (sid: string, pt: StrokePoint): void => {
      const state = holdTickRef.current;
      if (state === null || state.sid !== sid) return;
      state.lastPoint = pt;
      if (state.timer !== null) window.clearTimeout(state.timer);
      state.timer = window.setTimeout(fireHoldTick, AIRBRUSH_HOLD_TICK_INTERVAL_MS);
    };

    const toCanvasXY = (e: PointerEvent): { x: number; y: number } => {
      // 画面座標 → 真の canvas 座標 (zoom / pan / flip / 将来の回転を全て
      // `viewport.ts` §1b の変換行列が吸収する。ここに比率計算や un-flip は持たない)。
      return viewMapperRef.current.clientToCanvas(e.clientX, e.clientY);
    };

    const toCanvasPoint = (e: PointerEvent): StrokePoint => {
      const { x, y } = toCanvasXY(e);
      // 2026-05-09 (consult / architecture.md §19.15 / Step 5c): 送信側 mapping
      // を 2 軸独立に適用.
      // - `computeEffectivePressure` は `{ p, pAlpha }` を返す (Step 5c で 2 値化):
      //   * `p`      … width 軸 effective pressure
      //   * `pAlpha` … alpha 軸 effective pressure
      // - 数値が返れば mapping 済 (= ペン UI で `min` を 0.3 にしている場合、
      //   生 `e.pressure=0.5` は `0.3 + 0.7*0.5 = 0.65`).
      // - **片軸 OFF のときは `1.0` を明示返却** (consult 2026-05-09 ユーザ実機
      //   fb の fix): `undefined` だと drawing-core 側 fallback
      //   `pt.pAlpha ?? pt.p ?? 1.0` 経由で pt.p (= 太さ用の動的値) が alpha 軸に
      //   漏れて「濃さ筆圧 OFF にしても alpha が動的に変わる」バグになる. `1.0`
      //   を wire 上に明示送信することで drawing-core で当該軸固定 (= scale=1.0、
      //   ペン UI 設定そのまま) の意図が伝わる.
      // - undefined 軸 (= 両軸 OFF / 非筆圧デバイス / 範囲外 等) は `pt.{p, pAlpha}`
      //   を **omit**. JSON.stringify で undefined field は dropped されるため、
      //   protocol 上は backward-compat (= 旧 client / server とも互換).
      // - settings 未指定 (caller が pressure-aware でない) ならペン種デフォルト
      //   (`DEFAULT_PEN_PRESSURE[pen]`) で計算. Step 5c で `brush.alphaEnabled`
      //   が default OFF になったので、brush デフォルト挙動は `{ p: 数値,
      //   pAlpha: 1.0 }` (= palette A 値 100% で真っ黒線画).
      // - iPad Apple Pencil 軽接触時 stamp 最大化バグ修正: `e.pointerType` を
      //   渡して pen のとき `pressure=0` を valid 値扱い.
      // - 2026-05-29 hotfix (α 公開直前): pointerType !== 'pen' (= mouse / touch
      //   / unknown) は **常に両軸 undefined を返す** ように `computeEffectivePressure`
      //   を変更. Chrome は押下中マウスに pressure=0.5 を返す (W3C Pointer Events
      //   L3 仕様) ため、widthEnabled=ON で実描画が設定 width の半分になり、
      //   BrushCursorOverlay (= pen.width 直接表示) と不一致になっていた.
      //   筆圧 ON はペンタブ専用と再定義し、マウスではトグル状態に関係なく無効化.
      const config = penRef.current;
      const settings = pressureSettingsRef.current ?? DEFAULT_PEN_PRESSURE[config.pen];
      const pointerType = narrowPointerType(e.pointerType);
      const { p, pAlpha } = computeEffectivePressure(settings, e.pressure, pointerType, config.pen);
      // exactOptionalPropertyTypes 互換のため、undefined を含めずに組み立てる.
      // (`{ p: undefined }` を直接書くと exact-optional の strict typecheck で
      // `number | undefined` ≠ `number?` のエラーになる.)
      if (p !== undefined && pAlpha !== undefined) return { x, y, p, pAlpha };
      if (p !== undefined) return { x, y, p };
      if (pAlpha !== undefined) return { x, y, pAlpha };
      return { x, y };
    };

    /**
     * 通常ストロークの開始 (engine.startStroke + stroke.start 送信 + activeRef
     * セット + リード線 seed)。従来 pointerdown に直書きされていたブロックの抽出で、
     * pen / mouse の即時開始経路と、touch の猶予窓明け経路 (= バッファ点列持ち込み、
     * M1-c) の両方から呼ぶ。initialPts が1点のとき従来コードと等価 (挙動不変)。
     *
     * 2026-05-21 液タブ ペン尻 = 消しゴム (= Java 版踏襲, `penEraserTip.ts`):
     * - `eraserTip === true` のとき、この stroke の `pen` を 'eraser' で override する.
     * - UI 上の選択ペン (`penRef.current.pen`) は触らない (= ペン先に戻したら
     *   即元のペンで描ける、自動復帰).
     * - color / alpha / width / mask 等は UI 設定のまま流す (= drawing-core の
     *   eraser pen 実装が必要なフィールドだけ参照).
     * - 判定は pointerdown 時に 1 度だけ (= stroke 途中のペン先 ⇄ ペン尻 切替は無視).
     */
    const beginStrokeNow = (
      config: PenConfig,
      pointerId: number,
      initialPts: readonly StrokePoint[],
      eraserTip: boolean,
      input: StrokeInputKind | undefined,
    ): void => {
      const engine = engineRef.current;
      if (engine === null) return;
      // 範囲の道具: 線は描かず、ドラッグを範囲選択 / 浮かせた絵の移動として流す。猶予窓で溜めた
      // 点列 (touch) もここで順に流す。受け付けなければ以降の move / up を飲み込む ('blocked')。
      const region = regionPointerRef.current;
      if (region !== null) {
        const first = initialPts[0];
        if (first === undefined || !region.begin(first.x, first.y)) {
          activeRef.current = { kind: 'blocked', pointer: pointerId };
          return;
        }
        activeRef.current = { kind: 'region-drag', pointer: pointerId };
        for (const pt of initialPts.slice(1)) region.move(pt.x, pt.y);
        return;
      }
      // airbrush-design-2026-08-15.md §4-4 / §5-3: a fresh crypto-random seed
      // MUST be drawn exactly once per stroke and threaded through the SAME
      // `effectivePen` object that both `engine.startStroke` (below) and
      // `sendStrokeStart` (a few lines down) read via `penParamsFor` — see
      // `PenConfig.airbrushSeed` doc for why generating it inside
      // `penParamsFor` itself would make the two calls diverge.
      const effectivePen: PenConfig = eraserTip
        ? { ...config, pen: 'eraser' }
        : config.pen === 'airbrush'
          ? { ...config, airbrushSeed: generateAirbrushSeed() }
          : config;
      // texture preset (texture-brush-design.md §11 step 3). undefined for
      // non-texture pens (incl. pen-tip-flip eraser) → wire/engine omit it.
      const effectivePenParams = penParamsFor(effectivePen);
      const { sid } = engine.startStroke({
        pen: effectivePen.pen,
        color: effectivePen.color,
        alpha: effectivePen.alpha,
        width: effectivePen.width,
        layerId: effectivePen.layerId,
        mask: effectivePen.mask,
        // §9.9 AA: PenConfig 由来 (Classic=false / Modern=aaPreference)。
        // sendStrokeStart も pen.aa を載せるので wire とローカル描画が一致する。
        aa: effectivePen.aa,
        ...(effectivePenParams !== undefined ? { penParams: effectivePenParams } : {}),
      });
      // 入力手段 + 筆圧オンの計測 (pen-usage-analytics §12)。pressure は点列の `p` を
      // 作る toCanvasPoint と同じ設定 (pressureSettingsRef ?? config.pen の既定) を使う
      // (= 再読込で食い違わない)。ペン尻消しゴム化しても点に適用される設定は config.pen のもの。
      const appliedPressure = pressureSettingsRef.current ?? DEFAULT_PEN_PRESSURE[config.pen];
      sendStrokeStart(transport, {
        sid,
        pen: effectivePen,
        input,
        pressure: isPressureOn(appliedPressure),
      });
      const stroke: ActiveStroke = {
        sid,
        pending: [...initialPts],
        rafHandle: null,
        pen: effectivePen.pen,
        width: effectivePen.width,
      };
      activeRef.current = { kind: 'stroke', stroke, pointer: pointerId };
      // 2026-06-13 (feature/preview-perf): 通常 stroke 開始をプレビューへ通知
      // (= サムネ再生成サスペンド開始)。spuit / imagepaste-drag / blocked では呼ばない
      // (= 描画ストロークではないので、それらの間プレビューを止める必要はない)。
      onStrokeActiveChangeRef.current?.(true);

      // §9.8.1 — start a lead-line session (active, anchor reset). The SVG
      // overlay is display-no-op now (① 格上げ); the engine lead loop below is
      // what draws.
      leadLineSinkRef.current?.begin();

      // First point(s) immediately (touch 猶予窓経路ではバッファ全点)。
      scheduleFlush();

      // airbrush-design-2026-08-15.md §3: hold-to-build state, only for
      // `pen==='airbrush'` (§3 適用範囲 — 他ペンは holdTickRef が常に null のまま)。
      // Seed with the last of `initialPts` (= touch 猶予窓経路でも常に最新点) so the
      // first tick (after 50ms of silence) lands where the pen currently is.
      const holdTickSeedPt = initialPts[initialPts.length - 1];
      if (effectivePen.pen === 'airbrush' && holdTickSeedPt !== undefined) {
        holdTickRef.current = {
          sid,
          timer: window.setTimeout(fireHoldTick, AIRBRUSH_HOLD_TICK_INTERVAL_MS),
          lastPoint: holdTickSeedPt,
          streakCoord: { x: holdTickSeedPt.x, y: holdTickSeedPt.y },
          streakCount: 0,
        };
      } else {
        stopHoldTick();
      }

      // §9.8.1 ① — capture the active stroke's pen appearance for the lead and
      // seed the lead loop. eraser strokes (incl. pen-tip-flip) skip the lead:
      // a destination-out eraser has no color to preview (= `setLead` returns
      // a no-op in drawing-core, but we avoid even calling it). The lead only
      // runs while stabilisation is on (`leadEnabledRef`).
      const firstPt = initialPts[0];
      const lastPt = initialPts[initialPts.length - 1];
      if (
        leadEnabledRef.current &&
        effectivePen.pen !== 'eraser' &&
        firstPt !== undefined &&
        lastPt !== undefined
      ) {
        leadPenRef.current = {
          pen: effectivePen.pen,
          color: effectivePen.color,
          alpha: effectivePen.alpha,
          width: effectivePen.width,
          aa: effectivePen.aa,
          layerId: effectivePen.layerId,
          mask: effectivePen.mask,
          penParams: effectivePenParams,
        };
        // Seed the anchor with the pen-down point's pressure too (= 確定線と
        // 同じ筆圧→幅で先端を描く, 2026-06-27 fix).
        leadAnchorRef.current = {
          x: firstPt.x,
          y: firstPt.y,
          ...(firstPt.p !== undefined ? { p: firstPt.p } : {}),
          ...(firstPt.pAlpha !== undefined ? { pAlpha: firstPt.pAlpha } : {}),
        };
        // 猶予窓経路 (M1-c) では先頭以降のバッファ点が「anchor 以降の生入力」に相当。
        // 即時経路 (initialPts 1点) では空 = 従来と同一。
        leadRawTailRef.current = initialPts.slice(1).map(leadSampleOf);
        leadCursorRef.current = leadSampleOf(lastPt);
        startLeadLoop();
      }
    };

    // ------------------------------------------------------------------
    // M1-c タッチジェスチャ (猶予窓 + 2本指パン/ピンチ) の impure shell
    // (docs/_wip/modern-phone-shell-2026-07-02.md §2 確定B案 / §3.1 M1-c)。
    //
    // touch の pointerdown は即ストローク開始せず TOUCH_GRACE_WINDOW_MS の間
    // 点列をバッファに溜める。窓内に2本目が着地 → バッファ破棄 (stroke.start
    // 未送信なので protocol 完全無影響) → 2本指ジェスチャへ。窓経過 or 窓内
    // pointerup → バッファからストローク開始/確定。遷移判定は pure reducer
    // (`transitionTouchGesture`)、ここはその effects (タイマー / document
    // listener / engine 呼び出し) を実行するだけ。pen / mouse はこの機構を
    // 一切通らない (= 即時開始のまま、描き味・レイテンシ不変)。
    // ------------------------------------------------------------------

    /** 猶予窓タイマー破棄 (effects: cancel-window)。 */
    const cancelGraceWindow = (): void => {
      const pending = touchPendingRef.current;
      if (pending !== null && pending.timer !== null) {
        window.clearTimeout(pending.timer);
        pending.timer = null;
      }
    };

    /**
     * バッファからストローク開始 (effects: begin-stroke / commit-tap の実体)。
     * stroke.start の送信はこの時点 (= ジェスチャ化したバッファは wire に乗らない)。
     * 窓中に別経路 (mouse 等) が activeRef を取っていた縮退ケースでは安全側で破棄する。
     */
    const startPendingStroke = (): void => {
      const pending = touchPendingRef.current;
      touchPendingRef.current = null;
      if (pending === null) return;
      if (engineRef.current === null || activeRef.current !== null) {
        touchPhaseRef.current = IDLE_TOUCH_PHASE;
        endSuppressSelection();
        return;
      }
      // 猶予窓の間に再接続中になった: stroke を始めず、合図だけ出して破棄する。
      if (lockedRef.current) {
        touchPhaseRef.current = IDLE_TOUCH_PHASE;
        endSuppressSelection();
        onLockedPointerDownRef.current?.();
        return;
      }
      // 自分の範囲の移動を送信中 (確定 → echo まで): 新しい線は始めない (範囲の道具は線を描かない)。
      if (regionPointerRef.current === null && engineRef.current.isOwnStrokeInputHeld()) {
        touchPhaseRef.current = IDLE_TOUCH_PHASE;
        endSuppressSelection();
        return;
      }
      // touch にペン尻消しゴム判定は無い (`isPenEraserTipPointer` は pen 専用) → false。
      beginStrokeNow(pending.pen, pending.pointerId, pending.pts, false, 'touch');
    };

    /** rAF 1回ぶんのジェスチャ適用 (2指重心移動 → pan / 距離比 → pinch)。 */
    const applyGestureFrame = (): void => {
      const g = touchGestureRef.current;
      if (g === null) return;
      g.rafHandle = null;
      // 片指離脱後は凍結 (= 残り1本ではパンもピンチも描画もしない。全指離脱で解除)。
      if (g.pointers.size < 2) return;
      const it = g.pointers.values();
      const a = it.next().value;
      const b = it.next().value;
      if (a === undefined || b === undefined) return;
      const frame = computeGestureFrame(a, b, g.lastCentroid, g.startZoom, g.startDistance);
      g.lastCentroid = frame.centroid;
      if (frame.panDx !== 0 || frame.panDy !== 0) {
        onGesturePanRef.current?.(frame.panDx, frame.panDy);
      }
      // 絶対倍率で毎フレーム上書き (= 誤差蓄積なし)。pan を先に適用してから zoom を
      // 呼ぶ順序が仕様: handleZoomToAtPointer は呼び出し時点の scroll からアンカー
      // canvas 座標を算出するので、直前の pan がアンカー計算に織り込まれる。
      // clamp 後に現在値と同じなら API 側が no-op (= 純パン時も安全)。
      onGestureZoomToRef.current?.(frame.nextZoom, frame.centroid.x, frame.centroid.y);
      // ひねり (回転): 2 本指ベクトルの角度変化 → しきい値超過後に絶対回転角で通知。
      // pan → zoom → rotate の順なので、どれも重心の真下の canvas 点を動かさない。
      if (g.rotation !== null) {
        const nextRotation = g.rotation.update(vectorAngleDeg(a.x, a.y, b.x, b.y));
        if (nextRotation !== null) {
          onGestureRotateToRef.current?.(nextRotation, frame.centroid.x, frame.centroid.y);
        }
      }
    };

    const onGestureDocPointerMove = (e: PointerEvent): void => {
      const g = touchGestureRef.current;
      if (g === null || !g.pointers.has(e.pointerId)) return;
      e.preventDefault();
      g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // 2指 × 60Hz+ の move を「1 frame 1 適用」に rAF でコアレス
      // (= zoom setState / re-render の連打を避ける)。
      if (g.rafHandle === null) {
        g.rafHandle = window.requestAnimationFrame(applyGestureFrame);
      }
    };

    /**
     * 決定1 (§6): `touchGestureRef` セッションが「非 penOnly の猶予窓」/「penOnlyMode
     * の touch」のどちらの reducer 由来かで dispatch 先を分ける (`gestureOwnerRef`)。
     * 2本指の move/rAF 適用 (`applyGestureFrame` / `onGestureDocPointerMove`) は
     * どちらの owner でも共通なので分岐不要 (= セッションの構造自体が owner に
     * 依存しないため)。
     */
    const onGestureDocPointerFinish = (e: PointerEvent): void => {
      const g = touchGestureRef.current;
      if (g === null || !g.pointers.has(e.pointerId)) return;
      g.pointers.delete(e.pointerId);
      const type = e.type === 'pointercancel' ? 'touch-cancel' : 'touch-up';
      if (gestureOwnerRef.current === 'pen-only') {
        dispatchPenOnlyTouch({ type, pointerId: e.pointerId }, e);
      } else {
        dispatchTouch({ type, pointerId: e.pointerId }, e);
      }
    };

    /**
     * ジェスチャモード入り (effects: enter-gesture)。1本目の pointerId + 最終
     * client 位置と、2本目の pointerdown 位置で session を初期化する。
     * `firstPointerId`/`firstClient` は呼び出し元が (どちらの reducer でも)
     * 遷移前のフェーズから明示的に渡す — 非 penOnly の猶予窓は `touchPendingRef`
     * (点列バッファ付き)、penOnlyMode の touch は `watching` フェーズ自身
     * (バッファ無し) と保持場所が違うため、`enterGesture` 自体はどちらのデータ
     * 構造にも依存しない形にしてある (= 決定1 で追加した2本目の入口と共用するための
     * 一般化、既存の非 penOnly 経路の挙動は不変)。`owner` は `gestureOwnerRef` へ
     * 記録し、finish 時の dispatch 先を決める。
     *
     * 2本指の追跡は document capture listener で行う: canvas 上の touch は
     * document-level preventDefault 済で OS ジェスチャは来ない = PointerEvent の
     * 複数 pointerId 自前追跡 (§3.1)。2本目/移動が canvas 外 (fit ズームの余白等)
     * でも追える。
     */
    const enterGesture = (
      e: PointerEvent | undefined,
      firstPointerId: number,
      firstClient: GesturePoint,
      owner: 'window' | 'pen-only',
    ): void => {
      if (e === undefined) return; // defensive (来ない想定)
      // 計測 (§7): ジェスチャ入り = started (1ジェスチャ1回、両 owner 共通)。
      // callback は例外を投げない契約だが、念のため描画/ジェスチャを妨げない。
      try {
        onTouchGestureAnalyticsRef.current?.('started');
      } catch {
        // best-effort
      }
      const a: GesturePoint = { x: firstClient.x, y: firstClient.y };
      const b: GesturePoint = { x: e.clientX, y: e.clientY };
      const startZoomRaw = engineRef.current?.getZoom() ?? 1;
      gestureOwnerRef.current = owner;
      touchGestureRef.current = {
        pointers: new Map([
          [firstPointerId, a],
          [e.pointerId, b],
        ]),
        startZoom: startZoomRaw > 0 ? startZoomRaw : 1,
        startDistance: gestureDistance(a, b),
        rotation:
          onGestureRotateToRef.current === undefined
            ? null
            : createRotationTracker({
                startRotation: viewMapperRef.current.getTransform().rotation,
                startAngle: vectorAngleDeg(a.x, a.y, b.x, b.y),
                thresholdDeg: ROTATION_GESTURE_THRESHOLD_DEG,
                snapDeg: ROTATION_SNAP_DEG,
              }),
        lastCentroid: gestureCentroid(a, b),
        rafHandle: null,
      };
      document.addEventListener('pointermove', onGestureDocPointerMove, {
        capture: true,
        passive: false,
      });
      document.addEventListener('pointerup', onGestureDocPointerFinish, true);
      document.addEventListener('pointercancel', onGestureDocPointerFinish, true);
    };

    /**
     * ジェスチャ解除 (effects: exit-gesture、および unmount cleanup)。listener +
     * rAF を撤去する。解除後 touchGestureRef は null なので、残っていた指の move /
     * up は全ハンドラで早期 return になり誤描画しない (描画は新規 pointerdown で
     * しか始まらない)。penOnlyMode 中の pen 接地による強制終了 (決定1) でも使う。
     */
    const teardownGesture = (): void => {
      document.removeEventListener('pointermove', onGestureDocPointerMove, { capture: true });
      document.removeEventListener('pointerup', onGestureDocPointerFinish, true);
      document.removeEventListener('pointercancel', onGestureDocPointerFinish, true);
      const g = touchGestureRef.current;
      gestureOwnerRef.current = 'window';
      if (g === null) return;
      if (g.rafHandle !== null) {
        window.cancelAnimationFrame(g.rafHandle);
      }
      touchGestureRef.current = null;
      // pending / watching 入りで立てた select 抑止フラグをジェスチャ終端で解除する
      // (= pending → gesture / watching → gesture 経路では finish の一律解除を
      // 通らないため)。
      endSuppressSelection();
    };

    const runTouchEffect = (effect: TouchGestureEffect, e: PointerEvent | undefined): void => {
      switch (effect) {
        case 'start-window': {
          const pending = touchPendingRef.current;
          if (pending === null) return;
          pending.timer = window.setTimeout(() => {
            const current = touchPendingRef.current;
            if (current === null || current.pointerId !== pending.pointerId) return;
            current.timer = null;
            dispatchTouch({ type: 'window-elapsed' }, undefined);
          }, TOUCH_GRACE_WINDOW_MS);
          return;
        }
        case 'cancel-window':
          cancelGraceWindow();
          return;
        case 'begin-stroke':
        case 'commit-tap':
          // commit-tap は「開始 + 即確定」: ここで activeRef を立て、呼び出し元
          // (finish) の後段の通常 stroke 経路が flush + stroke.end まで進める
          // (= 窓内 pointerup の短タップも1点ストロークとして成立)。
          startPendingStroke();
          return;
        case 'discard-buffer':
          // stroke.start 未送信のまま破棄 (= protocol / server 完全無影響)。
          touchPendingRef.current = null;
          return;
        case 'enter-gesture': {
          // pending バッファの「1本目の最終位置」を読む (discard-buffer より先に
          // 実行される効果順序に依存、既存どおり)。
          const pending = touchPendingRef.current;
          if (pending === null) return;
          enterGesture(e, pending.pointerId, pending.lastClient, 'window');
          return;
        }
        case 'exit-gesture':
          teardownGesture();
          return;
      }
    };

    /** reducer に1イベント流し、返った effects を順に実行する (順序が仕様)。 */
    const dispatchTouch = (event: TouchGestureEvent, e: PointerEvent | undefined): void => {
      const transition = transitionTouchGesture(touchPhaseRef.current, event);
      touchPhaseRef.current = transition.phase;
      for (const effect of transition.effects) {
        runTouchEffect(effect, e);
      }
    };

    /**
     * 決定1 (§6): penOnlyMode 中の touch 状態機械 (`transitionPenOnlyTouch`) の
     * effects 実行。猶予窓が無いので `runTouchEffect` より小さい (start-window 等
     * 無し)。`enter-gesture` は遷移前の `watching` フェーズ (= 1本目の pointerId +
     * 位置) を必要とするため、呼び出し元 (`dispatchPenOnlyTouch`) から遷移前の
     * phase を受け取る。
     */
    const runPenOnlyTouchEffect = (
      effect: PenOnlyTouchEffect,
      prevPhase: PenOnlyTouchPhase,
      e: PointerEvent | undefined,
    ): void => {
      switch (effect) {
        case 'enter-gesture': {
          if (prevPhase.kind !== 'watching') return; // defensive (来ない想定)
          enterGesture(e, prevPhase.pointerId, prevPhase.lastClient, 'pen-only');
          return;
        }
        case 'exit-gesture':
          teardownGesture();
          return;
      }
    };

    /** 決定1 (§6): penOnly touch reducer に1イベント流し、effects を順に実行する。 */
    const dispatchPenOnlyTouch = (event: PenOnlyTouchEvent, e: PointerEvent | undefined): void => {
      const prevPhase = penOnlyTouchPhaseRef.current;
      const transition = transitionPenOnlyTouch(prevPhase, event);
      penOnlyTouchPhaseRef.current = transition.phase;
      for (const effect of transition.effects) {
        runPenOnlyTouchEffect(effect, prevPhase, e);
      }
    };

    /**
     * 猶予窓中の「2本目の touch」検知 + 決定1 (§6) penOnlyMode 中の「2本目の touch」
     * 検知 (document capture、常設)。2本目は canvas 要素の外 (fit ズームの余白 /
     * 枠) に着地することがあるため document で拾う。いずれも touch 以外は即 return
     * (= pen / mouse の既存経路に一切影響しない)。2本目が canvas 上に着地した場合は
     * capture phase (ここ) が先に走って gesture 入りし、後続の canvas pointerdown
     * ハンドラはジェスチャ黙殺ガードで抜ける (= 両経路共通の既存ガード、後述
     * `onPointerDown` 冒頭)。
     */
    const onDocumentPointerDown = (e: PointerEvent): void => {
      if (narrowPointerType(e.pointerType) !== 'touch') return;
      // 計測 (§7): 窓を逃して黙殺される2本目 (stroking 中の別 touch)。挙動は変えず
      // (reducer にも流さない)、1ストロークにつき最初の1回だけ late を通知する。
      const lateRaw = lateTouchDelayRaw(
        touchPhaseRef.current,
        e.pointerId,
        e.timeStamp,
        touchFirstDownRef.current,
      );
      if (lateRaw !== null && touchFirstDownRef.current !== null) {
        touchFirstDownRef.current = { ...touchFirstDownRef.current, lateReported: true };
        try {
          onTouchGestureAnalyticsRef.current?.('late', clampTouchGestureDelayMs(lateRaw));
        } catch {
          // best-effort
        }
      }
      const pending = touchPendingRef.current;
      if (pending !== null && e.pointerId !== pending.pointerId) {
        dispatchTouch({ type: 'touch-down', pointerId: e.pointerId }, e);
        return;
      }
      // 決定1 (§6): penOnlyMode 中の「1本目 watching」状態で document 上に2本目が
      // 着地 (fit 余白等、canvas 外) した場合もジェスチャに入れる。pen 接地中
      // (`penActiveRef`) は watching に入っていないはずだが念のため二重に弾く。
      if (penOnlyModeRef.current && !penActiveRef.current) {
        const watching = penOnlyTouchPhaseRef.current;
        if (watching.kind === 'watching' && e.pointerId !== watching.pointerId) {
          dispatchPenOnlyTouch(
            { type: 'touch-down', pointerId: e.pointerId, client: { x: e.clientX, y: e.clientY } },
            e,
          );
        }
      }
    };

    /**
     * 現在の engine に紐づく `SpuitPickCoalescer` を取得する (無ければ生成、engine が
     * 差し替わっていれば張り直す)。callback は hot-swap ref 越しに最新を読むので、
     * coalescer 自体は engine ごとに1個で良い。spuit callback (`onSpuitColor`) 未指定なら
     * null を返す (= spuit 無効)。
     *
     * 2026-06-13 (feature/spuit-drag-realtime): ドラッグ中の連続 pick を coalescing +
     * seq ガードで捌くため、旧 fire-and-forget の `tryHandleSpuit` から置換した
     * (`spuit.ts` の `SpuitPickCoalescer` 参照)。`onColor` が毎サンプルの色反映
     * (= `onSpuitColor`)、`onCommit` が確定の1色記録 (= `onSpuitCommit`)。
     */
    const getSpuitCoalescer = (): SpuitPickCoalescer | null => {
      const cb = onSpuitColorRef.current;
      if (cb === undefined) return null;
      const engine = engineRef.current;
      if (engine === null) return null;
      const existing = spuitCoalescerRef.current;
      // engine 同一性は coalescer 内部に閉じるので、ここでは「engine が居る間は使い回す」。
      // engine 差し替えは onEngineReady → engineRef.current 更新で起きるが、coalescer は
      // dispose 不要 (= 純 state machine、GC 任せ)。engine が変わったら作り直す。
      if (existing !== null && existing.engine === engine) return existing;
      const created = new SpuitPickCoalescer(engine, {
        // onColor / onCommit / onEraser は呼ばれる時点の最新 callback を参照する (hot-swap)。
        onColor: (rgb) => onSpuitColorRef.current?.(rgb),
        onCommit: (rgb) => onSpuitCommitRef.current?.(rgb),
        // spuit-sample-modes-2026-06-22: モード B α0 → 消しゴム切替 (色は変えない)。
        onEraser: () => onSpuitEraserRef.current?.(),
      });
      spuitCoalescerRef.current = created;
      return created;
    };

    /**
     * canvas 座標系で engine から 1px 色を吸って coalescer 経由で caller に流す。
     * 戻り値はスポイトが発動して通常のクリック処理を skip すべきか否か。
     *
     * 2026-06-13: 旧 fire-and-forget を coalescer の `request()` に置換。単発クリックも
     * ドラッグ連続 pick も同じ経路 (= in-flight 0 から1回 pick → 対象不変なら再 pick 無し)。
     * 色追従とレース排除は `SpuitPickCoalescer` 側で担保する。
     */
    const tryHandleSpuit = (e: PointerEvent | MouseEvent): boolean => {
      const coalescer = getSpuitCoalescer();
      if (coalescer === null) return false;
      const config = penRef.current;
      // PointerEvent 経由は型に clientX/Y が無い (= MouseEvent から継承)
      // が runtime には必ずある. toCanvasXY は PointerEvent しか期待しない
      // ため、ここで小さな型 narrow を許容する.
      // スポイトは `toCanvasXY` (PointerEvent 専用) を経由しない (MouseEvent も来る) が、
      // 変換は同じ mapper (= 反転中でも見た目位置の真の色を拾える、canvas-flip.md §2
      // 要検証③)。
      const { x, y } = viewMapperRef.current.clientToCanvas(e.clientX, e.clientY);
      // spuit-sample-modes-2026-06-22: 現在のサンプル元モードを request に乗せる
      // (= ドラッグ中にモードが変わっても最新 request の mode で pick される)。
      coalescer.request({ mode: spuitSampleModeRef.current, layerId: config.layerId, x, y });
      return true; // 発動扱いで通常クリック処理を skip
    };

    /**
     * スポイト操作の開始。coalescer の確定状態 (lastColor / pending / in-flight 世代) を
     * リセットし、この操作で拾った色だけを確定対象にする (= 前操作の遅延 resolve を畳む)。
     * 操作開始の pointerdown (持続モード / Alt・右クリックジェスチャ両経路) で呼ぶ。
     */
    const beginSpuit = (): void => {
      getSpuitCoalescer()?.begin();
    };

    /**
     * 2026-06-12 スポイト確定: 操作の release (= finish の spuit-drag 分岐) で
     * 「最近色に残す確定の1色」を1回通知する。coalescer が「最後にリクエストした位置の色」
     * を保持しているのでそれを流す。未解決 (= pointerup が pick 解決より先) なら pending を
     * 立て、最新世代の resolve 時に発火する (= 確定の取りこぼし防止)。一度も拾えていない
     * 操作では何もしない。
     */
    const commitSpuit = (): void => {
      getSpuitCoalescer()?.commit();
    };

    const onContextMenu = (e: MouseEvent): void => {
      // Phase 6.7 後追い: 右クリックでスポイト発動 + ブラウザ既定メニュー抑止.
      // onSpuitColor が undefined のときも preventDefault は走らせて OK
      // (= キャンバス上で右メニューが出ない方が描画 UX 的に好ましい) が、
      // 動作の予測性のため callback がある時だけ抑止する.
      if (onSpuitColorRef.current === undefined) return;
      // ブラウザ既定メニューの抑止は常に行う (= キャンバス上で右メニューを出さない)。
      e.preventDefault();
      // 2026-06-20 (`fix/spuit-pen-rightclick`): 初回ピックは pointerdown 側で済ませる
      // ようにした。マウス右クリックは pointerdown → contextmenu の順で両方発火するので、
      // 直近の pointerdown が既にピック済なら **二重ピックを skip** する (= 同色なので実害は
      // 小さいが coalescer 整合のためクリーンに)。ガードはここで一度消費する。
      //
      // ペンのバレル右クリックで `contextmenu` も飛んできた環境では、pointerdown 側で既に
      // 拾っているのでここは no-op。逆に Windows Ink で `contextmenu` が来ない環境でも
      // pointerdown で拾えているので問題ない (= 本 fix の主眼)。万一 pointerdown で button===2
      // が来ず `contextmenu` だけが来る環境では、ガードが立っていない (null) ので従来どおり
      // ここで初回ピックする (= フォールバック)。
      if (spuitPickedOnPointerDownRef.current !== null) {
        spuitPickedOnPointerDownRef.current = null;
        return;
      }
      tryHandleSpuit(e);
    };

    const onPointerDown = (e: PointerEvent): void => {
      if (activeRef.current !== null) return;
      // 決定1 (consult 2026-09-29、§6): pen 接地は「今 pen が描いているか」を素直に
      // 追跡する (penOnlyMode の ON/OFF に関わらず更新、消費側だけ penOnlyMode 限定)。
      // penOnlyMode 中は pen 接地が penOnlyMode 中の touch ジェスチャ/watching を
      // 強制終了する (= 描画中の手のひら付き直しでズーム/パンが暴発しないよう pen を
      // 優先。ジェスチャ中に pen が触れたら「ジェスチャを終了して pen を優先」)。
      // penOnlyMode OFF の既存経路 (pen は元々このガードの対象外) には触れない。
      if (narrowPointerType(e.pointerType) === 'pen') {
        penActiveRef.current = true;
        if (penOnlyModeRef.current && penOnlyTouchPhaseRef.current.kind !== 'idle') {
          if (touchGestureRef.current !== null && gestureOwnerRef.current === 'pen-only') {
            teardownGesture();
          }
          penOnlyTouchPhaseRef.current = IDLE_PEN_ONLY_TOUCH_PHASE;
        }
      }
      // M1-c: 2本指ジェスチャ中に着地した追加の touch は黙殺する (描画 / spuit の
      // いずれにも流さない)。解除 (全指離脱) 後は touchGestureRef が null なので
      // 通らない = 新しい touch は通常どおり猶予窓から始まる。pen / mouse はこの
      // ガードを通過する (= 既存経路不変)。
      if (touchGestureRef.current !== null && narrowPointerType(e.pointerType) === 'touch') {
        e.preventDefault();
        return;
      }
      const engine = engineRef.current;
      if (engine === null) return;

      // 2026-05-09 (consult): iPad Apple Pencil の標準ジェスチャ (= テキスト
      // ハイライトツール / scribble モード) が canvas 上で発火すると右下チャット
      // のテキストが選択されて `pointerleave` で stroke が中断される. CSS
      // (`touch-action: none` / `user-select: none` 等) と併せて pointerdown で
      // `preventDefault()` を呼ぶことで Pencil の選択ジェスチャを抑止する.
      // `setPointerCapture` は pointer event のルーティング制御だけで、ブラウザの
      // 選択ジェスチャ自体は止められない. spuit / preview-drag / 通常 stroke の
      // 全経路に共通で効かせて良い (= pointerdown でデフォルト挙動が要る経路は
      // 無い、contextmenu は別ハンドラで処理済).
      e.preventDefault();

      // 2026-05-09 ペン専用モード (Palm Rejection、consult 2026-05-09):
      //
      // 1. `pointerType === 'pen'` を検知したら **毎回** 自動 ON 復帰.
      //    「ペンで描いたら毎回 ON に戻る」「マウスで一時 OFF にしたユーザでも、
      //    ペンを使ったら ON 復帰」のシンプルなモデル.
      //    既に ON なら setState を skip (= StrictMode dev 二重実行で余計な
      //    再レンダリングを防ぐ). caller 側 (ClassicLayout) も冪等に書いてある
      //    が両側で防御.
      // 2. `penOnlyMode === true` + `pointerType !== 'pen'` のとき early return.
      //    stroke / spuit / preview-drag いずれも開始しない. activeRef も触らず、
      //    setPointerCapture も呼ばず、beginSuppressSelection も呼ばない.
      //    pointermove / pointerup は activeRef.current === null で既存 early
      //    return が動く想定 (= state machine の不整合を起こさない).
      if (shouldAutoEnablePenOnlyMode(e.pointerType)) {
        if (!penOnlyModeRef.current) {
          onPenAutoEnableRef.current?.();
        }
      }
      // 範囲の道具の間は指 (touch) も受ける: ペン専用モードの「指は無視 (ジェスチャ専用)」を外し、
      // 通常の猶予窓の経路 (1 本指 = 範囲のドラッグ / 2 本指 = パン・ピンチ・ひねり) に乗せる。
      const penOnlyForThisPointer =
        penOnlyModeRef.current &&
        !(regionPointerRef.current !== null && narrowPointerType(e.pointerType) === 'touch');
      if (shouldSkipPointerForPenOnlyMode(e.pointerType, penOnlyForThisPointer)) {
        // 決定1 (consult 2026-09-29、§6): penOnlyMode 中の touch は「ジェスチャ専用」
        // (旧: 全経路 skip、ピンチ/パン不可 = iPad FB「ズームが使いづらい」の一因)。
        //   - 1本指: 無視 (palm rejection 維持、下の分岐に入らずそのまま return)。
        //   - 2本指: 猶予窓なしで即ジェスチャ (`transitionPenOnlyTouch` の
        //     idle→watching→gesture、非 penOnly の猶予窓とは別状態機械)。
        //   - pen 接地中 (`penActiveRef`) は touch を全無視 (上のブロックで
        //     penOnlyTouchPhaseRef は既に idle にクリア済 — ここでは新規 watching
        //     に入らせないだけで良い)。
        // mouse は従来どおり完全無視 (この分岐に入らない)。
        if (narrowPointerType(e.pointerType) === 'touch' && !penActiveRef.current) {
          try {
            canvas.setPointerCapture(e.pointerId);
          } catch {
            // ignore
          }
          beginSuppressSelection();
          dispatchPenOnlyTouch(
            { type: 'touch-down', pointerId: e.pointerId, client: { x: e.clientX, y: e.clientY } },
            e,
          );
        }
        return;
      }

      // Phase 6.7 後追い: スポイトジェスチャ (Alt+クリック OR 右クリック) は
      // 通常 stroke を開始しない.
      //
      // 2026-06-20 (`fix/spuit-pen-rightclick`): 右クリック単体の初回ピックを
      // **pointerdown で即実行する** (= 旧 Java AWT `mousePressed(BUTTON3)` 踏襲)。
      // 旧実装は右クリックの初回ピックを後続 `contextmenu` 任せにしていたが、ペンの
      // バレル右クリックは Windows Ink 下で `contextmenu` を安定発火しないため初回
      // ピックが来ず「一瞬だけ効く」regression になっていた。`shouldPickOnPointerDown`
      // (= Alt OR button===2) はこのブロック (= `shouldSkipStrokeOnPointerDown`、同条件)
      // を通った時点で常に true なので、ここで一律に初回ピックする。
      if (shouldSkipStrokeOnPointerDown(e)) {
        // 2026-06-13: 新しいスポイト操作の開始で coalescer の確定状態 (lastColor /
        // pending / in-flight 世代) をリセットする。**初回 pick (tryHandleSpuit) より
        // 先に** 呼ぶ (= begin が in-flight を畳むので、リセット → pick の順でないと
        // 初回 pick が捨てられる)。
        beginSpuit();
        if (shouldPickOnPointerDown(e)) {
          tryHandleSpuit(e);
          // 直後に来る `contextmenu` (マウス右クリックは必ず発火) で同じ位置を二重に
          // 拾わないよう、この pointer の世代を記録する。`onContextMenu` は最新の
          // pointerdown ピックと一致したら自前のピックを skip する (= 二重ピックガード)。
          spuitPickedOnPointerDownRef.current = e.pointerId;
        }
        // 2026-05-06: ドラッグ中の連続スポイト (Photoshop 踏襲) を ON.
        // pointerdown で初回ピック済 + ここで drag モードに入ることで pointermove
        // で連続発動する。
        if (shouldEnterSpuitDragMode(e) && onSpuitColorRef.current !== undefined) {
          spuitDragRef.current = { pointer: e.pointerId };
          // 2026-05-09 (consult): spuit-drag も「描画ジェスチャ」扱いで
          // select 抑止フラグを立てる (= Pencil ドラッグ中の意図しない選択回避).
          beginSuppressSelection();
        }
        return;
      }

      // §9.6 スポイト持続モード (Modern 専用、2026-06-12): 素クリック (Alt 不要) で
      // 色拾い → ストローク非開始。選び直すまでモード継続 (= caller が state 保持)。
      // 上の Alt/右クリック瞬間ジェスチャ経路 (`shouldSkipStrokeOnPointerDown`) を通過した
      // = 修飾なしの素クリックなので、持続モード中ならここで色を拾って return する
      // (= activeRef を触らず stroke を開始しない)。
      //
      // 2026-06-12 改訂: 初回 `tryHandleSpuit(e)` に加えて **spuit-drag モードへ合流**
      // する (= 既存 Alt ドラッグ経路と同じ `spuitDragRef` 機構)。これで押したまま
      // ドラッグした間、既存の pointermove ハンドラが ON の間 連続で色拾いする
      // (Photoshop 等の eyedropper 踏襲)。Alt ドラッグ経路は `setPointerCapture` を
      // 取らずに成立している (= finish の release も不要、~893 行のコメント参照) ので、
      // 持続モードも同じく capture 無しで揃える (= 同等の堅牢性、無理に capture を足さない)。
      //
      // **ライフサイクル分離**: `spuitDragRef` は 1 ドラッグの寿命 (pointerup/cancel/leave
      // で OFF)、`spuitModeRef` はツール選択の寿命 (別ツール選択まで継続)。finish では
      // `spuitDragRef` だけ落とし、`spuitModeRef` は触らない (= ドラッグを離しても持続
      // モードは維持され、次のドラッグでまた拾える)。
      if (spuitModeRef.current) {
        // 2026-06-13 スポイト確定: 新しい操作の開始で coalescer の確定状態をリセットしてから
        // 拾う (= この操作で拾った色だけを確定対象にする)。begin → request の順 (begin が
        // in-flight を畳むので初回 pick が捨てられないように)。
        beginSpuit();
        tryHandleSpuit(e);
        if (onSpuitColorRef.current !== undefined) {
          spuitDragRef.current = { pointer: e.pointerId };
          beginSuppressSelection();
        }
        return;
      }

      // Phase 7 Task 7-8b: preview-active なら drag を試す.
      if (engine.isImagepastePreviewActive()) {
        const { x, y } = toCanvasXY(e);
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        const consumed = engine.tryBeginImagepasteDrag(x, y);
        activeRef.current = consumed
          ? { kind: 'imagepaste-drag', pointer: e.pointerId }
          : { kind: 'blocked', pointer: e.pointerId };
        // 2026-05-09 (consult): drag 経路でも select 抑止フラグを立てる.
        // `blocked` (= preview rect 外でクリックを swallow) では立てなくても
        // 良いが、判定分岐を増やさず一律 ON にする (= pointerup の finish で
        // どちらの kind でも一律 OFF にされるので副作用なし).
        beginSuppressSelection();
        return;
      }

      // 通常の stroke フロー (preview なし).
      //
      // M1-c 猶予窓 (modern-phone-shell §2 確定B案): `pointerType === 'touch'` のみ、
      // 即ストローク開始せず TOUCH_GRACE_WINDOW_MS の間 点列をバッファに溜める
      // (先頭点もバッファへ)。stroke.start の送信は窓明け (begin-stroke) / 窓内
      // pointerup (commit-tap) の時点。窓内に2本目が着地したら document capture 側
      // (`onDocumentPointerDown`) が gesture 入りし、バッファは stroke.start 未送信の
      // まま破棄される (= protocol 完全無影響)。pen / mouse はこの分岐を通らない
      // (= 下の即時開始のまま、描き味・レイテンシ完全不変)。
      // 再接続中は描けない: 新しい stroke を始める入口はここ 1 箇所 (mouse / pen)。
      // スポイト・画像プレビューのドラッグは自分の画面で完結するので上の分岐で処理済み。
      // touch は 2 本指ジェスチャ (パン / ズーム) を生かすため猶予窓の経路に乗せ、
      // 窓明けの `startPendingStroke` で止める (そこでも合図を出す)。
      if (lockedRef.current && narrowPointerType(e.pointerType) !== 'touch') {
        onLockedPointerDownRef.current?.();
        return;
      }
      // 自分の範囲の移動を送信中 (確定 → echo まで): 新しい線は始めない (touch は窓明けの
      // `startPendingStroke` で止める。範囲の道具は線を描かないので対象外)。
      if (
        regionPointerRef.current === null &&
        narrowPointerType(e.pointerType) !== 'touch' &&
        engine.isOwnStrokeInputHeld()
      ) {
        return;
      }
      if (narrowPointerType(e.pointerType) === 'touch') {
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        // 2026-05-09 (consult): 描画ジェスチャ扱いで select 抑止フラグを立てる
        // (pending → stroke 昇格時は finish の一律解除、gesture 化時は
        // teardownGesture、破棄時は各経路で解除する)。
        beginSuppressSelection();
        touchFirstDownRef.current = {
          pointerId: e.pointerId,
          timeStamp: e.timeStamp,
          lateReported: false,
        };
        touchPendingRef.current = {
          pointerId: e.pointerId,
          // ペン設定は pointerdown 時点の snapshot (= 即時開始経路と同じ時点の設定)。
          pen: penRef.current,
          pts: [toCanvasPoint(e)],
          lastClient: { x: e.clientX, y: e.clientY },
          timer: null,
        };
        dispatchTouch({ type: 'touch-down', pointerId: e.pointerId }, e);
        return;
      }

      canvas.setPointerCapture(e.pointerId);
      // 2026-05-09 (consult): 通常 stroke 開始時にも select 抑止フラグを
      // 立てる. `pointerup` / `pointercancel` / `pointerleave` の `finish` で
      // 一律 OFF.
      beginSuppressSelection();
      // ペン尻消しゴム判定 (pointerdown 時に 1 度だけ) + ストローク開始。
      // 実体は `beginStrokeNow` (M1-c で touch 猶予窓経路と共通化した抽出、挙動不変)。
      if (isInputLatencyEnabled()) noteInputLatencyMark(e.timeStamp, true);
      beginStrokeNow(
        penRef.current,
        e.pointerId,
        [toCanvasPoint(e)],
        isPenEraserTipPointer(e),
        strokeInputFromPointerType(e.pointerType),
      );
    };

    const onPointerMove = (e: PointerEvent): void => {
      // M1-c 猶予窓中: 点列をローカルバッファに溜めるだけ (engine / transport は
      // 未接触)。窓明けにバッファごとストロークへ持ち込む。
      const pendingTouch = touchPendingRef.current;
      if (pendingTouch !== null && pendingTouch.pointerId === e.pointerId) {
        e.preventDefault();
        pendingTouch.pts.push(toCanvasPoint(e));
        pendingTouch.lastClient = { x: e.clientX, y: e.clientY };
        return;
      }

      // 2026-05-06: spuit drag モード中は連続スポイト. activeRef とは相互排他
      // (= spuit drag 中は pointerdown で stroke 開始を skip 済 = activeRef null).
      // Alt キーをドラッグ中に離されてもこの分岐は altKey を見ないので継続する
      // (Photoshop 等の eyedropper 踏襲, `shouldEnterSpuitDragMode` の docstring 参照).
      const spuit = spuitDragRef.current;
      if (spuit !== null && spuit.pointer === e.pointerId) {
        // 2026-05-09 (consult): iPadOS の Apple Pencil 連続水平 sweep が
        // 「テキスト選択 / Scribble」ジェスチャに化けて 2 回目以降の pointer
        // event が止まる現象への対策. spuit-drag 経路でも同じ問題が起きうる
        // ので一律 preventDefault で OS ジェスチャを抑止する.
        e.preventDefault();
        tryHandleSpuit(e);
        return;
      }

      const active = activeRef.current;
      if (active === null || active.pointer !== e.pointerId) return;
      // 2026-05-09 (consult): active stroke (通常 stroke / imagepaste-drag /
      // blocked) 中の pointermove で OS ジェスチャ (= iPadOS の連続水平 sweep を
      // テキスト選択ジェスチャと誤判定する挙動) を抑止する. canvas の
      // `touch-action: none` + pointerdown `preventDefault()` だけでは間に合わず、
      // pointermove も明示的に消費する必要がある.
      e.preventDefault();
      switch (active.kind) {
        case 'stroke': {
          const pt = toCanvasPoint(e);
          active.stroke.pending.push(pt);
          if (isInputLatencyEnabled()) noteInputLatencyMark(e.timeStamp, false);
          scheduleFlush();
          // airbrush-design-2026-08-15.md §3.4: a real pointermove resets the
          // 50ms hold-tick debounce (no-op unless this stroke is airbrush).
          noteHoldTickRealPoint(active.stroke.sid, pt);
          // §9.8.1 ① — track the raw cursor + accumulate the gap's raw tail so
          // the lead loop can trace 確定平滑フロンティア → 生入力 → カーソル.
          // Bounded: the tail is reset on every flush (publishLeadAnchor), so
          // it only ever holds one rAF's worth of samples.
          if (leadEnabledRef.current && leadPenRef.current !== null) {
            const sample = leadSampleOf(pt);
            leadCursorRef.current = sample;
            leadRawTailRef.current.push(sample);
          }
          return;
        }
        case 'imagepaste-drag': {
          const engine = engineRef.current;
          if (engine === null) return;
          const { x, y } = toCanvasXY(e);
          engine.updateImagepasteDrag(x, y);
          return;
        }
        case 'region-drag': {
          const { x, y } = toCanvasXY(e);
          regionPointerRef.current?.move(x, y);
          return;
        }
        case 'blocked':
          return;
      }
    };

    const finish = (e: PointerEvent): void => {
      // 決定1 (§6): pen 接地の終了 (pointerup/cancel/leave のいずれでも)。他の分岐に
      // 関わらず一律更新する (= 「pen が今まさに描いているか」を素直に追う)。
      if (narrowPointerType(e.pointerType) === 'pen') {
        penActiveRef.current = false;
        lastPenUpAtRef.current = Date.now();
      }

      // 2026-05-06: spuit drag モード中の pointerup/cancel/leave で OFF にする.
      // この分岐は activeRef === null でも実行する (= spuit 中は activeRef は
      // 常に null だから).
      const spuit = spuitDragRef.current;
      if (spuit !== null && spuit.pointer === e.pointerId) {
        spuitDragRef.current = null;
        // 2026-06-20 (`fix/spuit-pen-rightclick`): contextmenu が来ないまま操作が終わった
        // (= ペン Ink で `contextmenu` 非発火) 場合に、二重ピックガードが次操作へ漏れない
        // ようここでクリアする。マウスは contextmenu 側で既に消費済 (= ここでは null)。
        spuitPickedOnPointerDownRef.current = null;
        // 2026-06-12 スポイト確定 (最近色厳密化): 操作の release でこの操作の最終色を
        // 1回だけ commit する (= ドラッグ通過色・静止色は記録せず確定の1色だけ最近色へ)。
        commitSpuit();
        // 2026-05-09 (consult): spuit-drag も描画ジェスチャ扱いだったので
        // ここで select 抑止フラグを解除する.
        endSuppressSelection();
        // pointerCapture を取っていないので release も不要 (= spuit 中は
        // pointerdown で setPointerCapture を呼ばずに早期 return している).
        return;
      }

      // 決定1 (§6): penOnlyMode 中の「1本指 watching」(= まだジェスチャに昇格して
      // いない単独 touch) の pointerup/cancel/leave。ストロークは一切開始していない
      // ので、状態機械へ反映して抜けるだけで良い (昇格済み = phase は既に 'gesture'
      // なのでここは通らず、document 側の `onGestureDocPointerFinish` が扱う)。
      if (narrowPointerType(e.pointerType) === 'touch' && penOnlyModeRef.current) {
        const penOnlyPhase = penOnlyTouchPhaseRef.current;
        if (penOnlyPhase.kind === 'watching' && penOnlyPhase.pointerId === e.pointerId) {
          // 単独の指が (ジェスチャに昇格せず) 離れた = 無視されて終わった。pointerup のみ
          // (cancel / leave は OS 都合や領域外なので出さない)。
          if (e.type === 'pointerup') {
            const now = Date.now();
            if (
              shouldShowPenOnlyFingerNotice({
                now,
                lastShownAt: lastFingerNoticeAtRef.current,
                penActive: penActiveRef.current,
                lastPenUpAt: lastPenUpAtRef.current,
              })
            ) {
              lastFingerNoticeAtRef.current = now;
              onPenOnlyFingerIgnoredRef.current?.();
            }
          }
          dispatchPenOnlyTouch(
            {
              type: e.type === 'pointercancel' ? 'touch-cancel' : 'touch-up',
              pointerId: e.pointerId,
            },
            e,
          );
          try {
            canvas.releasePointerCapture(e.pointerId);
          } catch {
            // ignore
          }
          endSuppressSelection();
          return;
        }
      }

      // M1-c 猶予窓中の pointerup / pointercancel:
      //   - pointercancel → バッファ破棄 (ストローク不成立、stroke.start 未送信)。
      //   - pointerup / pointerleave (= 窓内短タップ) → commit-tap: バッファから
      //     ストローク開始 (activeRef が立つ) → 下の通常 stroke 経路へ fallthrough
      //     して flush + stroke.end まで進む (= 1点ストロークとして成立)。
      const pendingTouch = touchPendingRef.current;
      if (pendingTouch !== null && pendingTouch.pointerId === e.pointerId) {
        if (e.type === 'pointercancel') {
          dispatchTouch({ type: 'touch-cancel', pointerId: e.pointerId }, e);
          try {
            canvas.releasePointerCapture(e.pointerId);
          } catch {
            // ignore
          }
          endSuppressSelection();
          return;
        }
        dispatchTouch({ type: 'touch-up', pointerId: e.pointerId }, e);
        // fallthrough: commit-tap が activeRef を立てるので下の 'stroke' 経路が確定処理する。
      }

      const active = activeRef.current;
      if (active === null || active.pointer !== e.pointerId) return;
      switch (active.kind) {
        case 'stroke': {
          if (active.stroke.rafHandle !== null) {
            window.cancelAnimationFrame(active.stroke.rafHandle);
            active.stroke.rafHandle = null;
          }
          // airbrush-design-2026-08-15.md §3: stop the hold-tick timer chain
          // (no-op unless this stroke was airbrush). Must happen before the
          // stroke ends so no stray tick fires against a now-inactive sid.
          stopHoldTick();
          const engine = engineRef.current;
          if (active.stroke.pending.length > 0) {
            // §9.8 — send the engine's filtered output, not raw pending (see
            // `flushPending`). identity filter (off) ⇒ filtered == input.
            const toSend =
              engine !== null
                ? engine.addPoints(active.stroke.sid, active.stroke.pending)
                : active.stroke.pending;
            if (toSend.length > 0) {
              sendStrokeSegment(transport, { sid: active.stroke.sid, pts: toSend });
            }
            // §9.8.1 — keep the lead anchor at the latest stabilised point.
            publishLeadAnchor(toSend);
            active.stroke.pending = [];
          }
          // §9.8 — the flush tail (stabiliser convergence to the lift point)
          // must reach peers/snapshot BEFORE stroke.end. identity ⇒ empty tail.
          const tail = engine !== null ? engine.endStroke(active.stroke.sid) : [];
          if (tail.length > 0) {
            sendStrokeSegment(transport, { sid: active.stroke.sid, pts: tail });
          }
          sendStrokeEnd(transport, { sid: active.stroke.sid });
          // §9.8.1 — stroke ended: stop the lead loop + wipe the lead. The
          // committed body is already correct (the flush tail converged to the
          // lift point), so the lead just disappears (§9.8.1 ① ペンアップ
          // 再描画不要). Also notify the (display-no-op) SVG sink.
          stopLeadLoop();
          leadLineSinkRef.current?.end();
          // 2026-06-13 (feature/preview-perf): 通常 stroke 確定をプレビューへ通知
          // (= サムネ再生成サスペンド解除 → 次のスロットル機会で 1 回追いつき描画)。
          onStrokeActiveChangeRef.current?.(false);
          if (e.type !== 'pointercancel') {
            onOwnStrokeEndRef.current?.({ pen: active.stroke.pen, width: active.stroke.width });
          }
          break;
        }
        case 'imagepaste-drag': {
          engineRef.current?.endImagepasteDrag();
          break;
        }
        case 'region-drag': {
          const region = regionPointerRef.current;
          if (e.type === 'pointercancel') {
            region?.cancel();
          } else {
            const { x, y } = toCanvasXY(e);
            region?.end(x, y);
          }
          break;
        }
        case 'blocked':
          break;
      }
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      activeRef.current = null;
      // 2026-05-09 (consult): stroke / imagepaste-drag / blocked のいずれの
      // kind でも、pointerdown 経路で `beginSuppressSelection` を呼んで
      // フラグを立てているのでここで一律解除する.
      endSuppressSelection();

      // M1-c: 窓明け (begin-stroke) で始まった touch ストロークの確定を状態機械へ
      // 反映する (stroking → idle、effects なし = flush/end は上の経路が処理済)。
      // pen / mouse のストロークでは phase は idle のままなので no-op。
      const touchPhase = touchPhaseRef.current;
      if (touchPhase.kind === 'stroking' && touchPhase.pointerId === e.pointerId) {
        dispatchTouch(
          {
            type: e.type === 'pointercancel' ? 'touch-cancel' : 'touch-up',
            pointerId: e.pointerId,
          },
          e,
        );
      }
    };

    // 描いている最中に再接続中になった: pointerup を待たずその stroke を確定させる
    // (= pointercancel 相当)。以後の本物の pointerup は activeRef が null なので no-op。
    forceEndStrokeRef.current = (): void => {
      const active = activeRef.current;
      if (active === null || active.kind !== 'stroke') return;
      finish({
        pointerId: active.pointer,
        pointerType: 'mouse',
        type: 'pointercancel',
      } as unknown as PointerEvent);
    };

    // 2026-05-09 (consult): Pointer events と並行して、Touch events (= legacy)
    // でも preventDefault を呼ぶ. iPad Safari の OS ジェスチャ判定 (= Apple
    // Pencil Selection / Scribble) は `touchstart` / `touchmove` の段階で発火
    // するため、Pointer events の preventDefault では遅すぎる (= synthesize
    // される pointerdown / pointermove のときにはもう OS ジェスチャ判定が
    // 走り終わっている). ここで legacy Touch events を `passive: false` で
    // attach + `preventDefault()` することで OS ジェスチャ起動前に止める.
    // Pointer events は touch events から synthesize されるので、既存の
    // 描画ロジック (= startStroke / addPoints / endStroke) は影響を受けない.
    // 副作用: canvas 上の pinch zoom / 2 本指 scroll は無効化される (= 既に
    // viewport `user-scalable=no` で抑制済、影響軽微).
    const onTouchStart = (e: TouchEvent): void => {
      e.preventDefault();
    };
    const onTouchMove = (e: TouchEvent): void => {
      e.preventDefault();
    };
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', finish);
    canvas.addEventListener('pointercancel', finish);
    canvas.addEventListener('pointerleave', finish);
    canvas.addEventListener('contextmenu', onContextMenu);
    // M1-c: 猶予窓中の「2本目の touch」検知 (document capture、常設)。pending 中
    // 以外・touch 以外は即 return するので pen / mouse / 通常時のコストはほぼゼロ。
    document.addEventListener('pointerdown', onDocumentPointerDown, true);

    return () => {
      // M1-c: 猶予窓タイマー / ジェスチャ追跡 listener の後片付け (mid-gesture
      // unmount / StrictMode double-mount)。バッファは stroke.start 未送信のまま
      // 破棄されるので protocol 上のリークは無い。
      document.removeEventListener('pointerdown', onDocumentPointerDown, true);
      cancelGraceWindow();
      touchPendingRef.current = null;
      teardownGesture();
      touchPhaseRef.current = IDLE_TOUCH_PHASE;
      // 決定1 (§6): penOnlyMode 中の touch 状態機械 / pen 接地フラグも unmount で
      // リセットする (mid-gesture unmount / StrictMode double-mount 対策、上の
      // touchPhaseRef と同じ理由)。
      penOnlyTouchPhaseRef.current = IDLE_PEN_ONLY_TOUCH_PHASE;
      penActiveRef.current = false;
      forceEndStrokeRef.current = null;
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', finish);
      canvas.removeEventListener('pointercancel', finish);
      canvas.removeEventListener('pointerleave', finish);
      canvas.removeEventListener('contextmenu', onContextMenu);
      // §9.8.1 ① — cancel any in-flight lead rAF on unmount (mid-stroke
      // dispose / StrictMode double-mount) so the loop doesn't leak. We don't
      // call `clearLead` here: the engine may already be disposing, and
      // `stopLeadLoop`'s guarded `engineRef.current?.clearLead()` would race.
      if (leadRafRef.current !== null) {
        window.cancelAnimationFrame(leadRafRef.current);
        leadRafRef.current = null;
      }
      // airbrush-design-2026-08-15.md §3: cancel any in-flight hold-tick timer
      // on unmount (mid-stroke dispose / StrictMode double-mount), same reason
      // as the lead rAF above.
      stopHoldTick();
      // 2026-05-09 (consult): unmount 時にもフラグを解除. StrictMode dev
      // double-mount や、pointerdown 直後に component が disposed されるレース
      // でフラグが永久に残るのを防ぐ (= 残ると body 全体で text 選択不可になる).
      endSuppressSelection();
      // 2026-06-13 (feature/preview-perf): mid-stroke unmount でストローク進行中
      // シグナルが true のまま残るとプレビューが恒久サスペンドになるので解除する。
      onStrokeActiveChangeRef.current?.(false);
    };
  }, [transport]);

  return (
    <canvas
      ref={setCanvasRef}
      data-oekaki-canvas
      width={width}
      height={height}
      // CSS sizing matches buffer 1:1 in MVP.
      // 2026-05-09 (consult): iPad Apple Pencil の標準ジェスチャ (テキスト
      // ハイライト / scribble / long-press メニュー) が canvas 上で発火するのを
      // 抑止. これが無いと描画中に右下チャット領域のテキストが選択されて
      // `pointerleave` で stroke が中断されるバグが出る. `setPointerCapture` +
      // pointerdown 側の `preventDefault()` と併用で完全に止まる.
      // - touch-action: none      … iOS のスクロール / pan / zoom ジェスチャ抑止
      // - user-select: none       … テキスト選択ジェスチャ抑止
      // - WebkitUserSelect: none  … Safari (iPad) prefix
      // - WebkitTouchCallout: none… long-press の「画像を保存」メニュー抑止
      // imageRendering: pixelated … 初期表示から最近傍補間にして 1px 線のにじみを
      //   防ぐ. applyZoomStyle() は setZoom 経由でしか走らないため初回は適用されず,
      //   ズーム +/- 往復まで bilinear 補間のままになる問題への対策 (applyZoomStyle 非依存).
      //
      // 2026-06-09 fix/zoom-scroll-repaint (α-feedback K 詰め): 枠線を Tailwind
      //   `border` (= 実 border, layout に寄与) から **inset box-shadow** に置換.
      //   理由:
      //   - `border` は box-sizing:content-box(default)で border-box を
      //     `width + 2px` に広げる. canvas は engine が `transform: scale(zoom)`
      //     (transform-origin:0 0) で拡大するので border-box は視覚上
      //     `(width + 2) * zoom` まで伸び, 親 innerWrapper(box = `width * zoom`,
      //     `overflow: clip`)を右下に border ぶんはみ出す. clip がそのはみ出しを
      //     切り + scroll container の scrollWidth/Height からも除外するため,
      //     ドローイング最右・最下が拡大時に到達不能になる回帰が出ていた
      //     (はみ出し量は zoom 比例で悪化).
      //   - `box-shadow: inset` は layout box を一切変えない(border-box =
      //     content-box = `width`). よって innerWrapper(`width * zoom`)と canvas
      //     border-box(`width * zoom`)が一致し, はみ出し・到達不能が解消する.
      //   - inset 影は `border-radius`(= `rounded`)に追従するので角丸枠の見た目を
      //     維持する. drop shadow(旧 `shadow-lg`)も同じ box-shadow プロパティに
      //     第 2 値以降で併記して残す(Tailwind shadow-lg 相当値).
      //   - canvas attr `width/height`(= buffer 解像度)と CSS `width/height`
      //     (= content-box)は従来通り 1:1 なので, pointer 座標 mapping
      //     (`x = (clientX - rect.left) / rect.width * width`)は変わらない.
      //     むしろ旧 border は rect.width に 2px を混ぜて 1px 弱の座標スキューを
      //     生んでいたが, それも解消する(rect.width === content width).
      style={{
        width,
        height,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        imageRendering: 'pixelated',
        // §9.6 スポイト持続モード中は crosshair で「色拾いモード」を示す (任意の親切)。
        // モード外では指定なし (= 既定カーソル、Classic 含め無影響)。
        ...(spuitMode === true ? { cursor: 'crosshair' } : {}),
        // 範囲の道具の間も crosshair (範囲をドラッグで選ぶ)。
        ...(regionPointer !== null ? { cursor: 'crosshair' } : {}),
        // 2026-06-20 canvas-background-color CSS 方式: 背景可視 (👁 ON) のとき、現在の
        // 背景色を inline background-color に当てる。canvas は透明に描かれるので未描画部が
        // この色になる (= styles.css の白 `--color-canvas` 固定を上書きし backgroundRgb を
        // 反映)。color 変更は worker/repaint なしにこの inline style 更新だけで瞬時に効く。
        // 背景非可視 (👁 OFF) のときは inline を当てず、canvas-bg-checker class に市松を
        // 任せる (= 透過確認)。capture/export は engine 経路で実 bg を焼くので CSS 非関与。
        ...(backgroundVisible === false
          ? {}
          : { backgroundColor: rgbToCss(backgroundRgb ?? 0xffffff) }),
        // inset 1px 枠(layout 非依存)+ 旧 shadow-lg 相当の drop shadow.
        boxShadow:
          'inset 0 0 0 1px var(--color-canvas-frame), 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      }}
      // 透過確認チェッカー (canvas-background-color §8.4): 背景非可視のときだけ
      // `canvas-bg-checker` を付け、styles.css で背景を市松に差し替える (透過確認)。
      // 可視時は class を付けず、上の inline `backgroundColor`(= backgroundRgb)が
      // canvas の背景になる (engine は bg を焼かず透明に描く CSS 方式、2026-06-20)。
      className={backgroundVisible === false ? 'rounded canvas-bg-checker' : 'rounded'}
      role="img"
      aria-label="お絵描きキャンバス"
    />
  );
};
